/* ============================================================
   TV PROVIDER — the store. Owns the reducer, the engine
   instances (Tube / Snow / TVAudio), every timer and the whole
   imperative TV controller ported from the prototype's app.js.

   Components never touch any of this directly: they read `state`
   via useTv() and call `act()` / the callback refs. The reducer
   stays pure (tvMachine.ts); this layer performs the side effects
   (YouTube calls, foley, CSS flashes, timers) in response to the
   semantic verbs and to state transitions.
   ============================================================ */
import {
  useEffect,
  useMemo,
  useReducer,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactNode,
} from 'react'
import { TvContext, type RemoteAction, type TvApi } from './context'
import { createTvMachine, type TvAction } from './tvMachine'
import { buildInitialState, savePersisted, EXTENSION_HINT_KEY } from './initialState'
import { syncUrl } from './url'
import { SESSION_ID } from '@/session'
import { resolveBreak as pureResolveBreak, programOrdinalToQueueIndex, queueIndexToProgramOrdinal } from './breakResolver'
import { buildChannelQueue, buildQueueFromVideos, queueEntries } from './queue'
import { update as engineUpdate, type Env as EngineEnv } from './engine/update'
import { initialPlaybackModel, type PlaybackModel } from './engine/model'
import type { Effect, TimerId } from './engine/effects'
import type { PlaybackEvent, EventContext } from './engine/events'
import { buildLiveQueue, liveSlot, isLiveCapable, channelRandom } from './live'
import { searchGuide } from './guideSearch'
import { deviceBodyClasses } from './deviceClass'
import { classifySwipe } from './swipe'
import { BumpAudio } from '@/bumps/audio'
import { DualPlayer } from '@/engine/dualPlayer'
import { Snow } from '@/engine/snow'
import { TVAudio } from '@/engine/audio'
import * as Sentry from '@sentry/react'
import type { Channel, QueueItem, TvState } from './types'
import cabinetStyles from '@/components/Cabinet.module.css'
import remoteStyles from '@/components/Remote.module.css'
import screenStyles from '@/components/Screen.module.css'
import touchStyles from '@/components/Touchscreen.module.css'

// LIVE as timeline proximity: you ARE live while your position on the broadcast
// loop is within this many seconds of the live line (either direction). Inside
// the band we track to the exact line; outside it you're off-sync and left alone.
// Live-tracking thresholds live in the engine (single source of truth, shared with the pure core).
// LIVE_LOCK/LOSE form a Schmitt trigger; SEEK_COOLDOWN avoids a stutter loop; LAG_DEADBAND ignores
// sub-tolerance jitter; LIVE_STALL_GRACE tolerates a transient spin-up blip. See engine/constants.
const BUMPER_CHANNEL_MS = 3000
const BUMPER_PROGRAM_MS = 1800
const NO_SIGNAL_WASH_MS = 280
const VOL_STEP = 7
const AD_CONFIRM_W = 180
const AD_CONFIRM_H = 52
const AD_CONFIRM_MARGIN = 6
const AD_CONFIRM_FLEE_RADIUS = 105
const AD_CONFIRM_ACCEL = 26
const AD_CONFIRM_DAMPING = 0.58
const AD_CONFIRM_MAX_SPEED = 48
// Idle wander (when the pointer isn't near): a gentle random drift.
const AD_CONFIRM_IDLE_ACCEL = 7
const AD_CONFIRM_IDLE_DAMPING = 0.9
const AD_CONFIRM_IDLE_MAX_SPEED = 6
const AD_CONFIRM_IDLE_MS = 80
const AD_CONFIRM_LABELS = [
  'CONFIRM DISABLING ADS',
  'PLEASE THINK OF THE POOR CEOS',
  'AHAHAH CATCH ME',
  '-1000 SOCIAL CREDIT',
  'WHEEEEEEE',
  'HOW ELSE WILL YOU KNOW WHAT TO BUY',
  'WANTON CAPITALISM HO!',
]

type PlaybackEventSeed =
  | { t: 'Tick' }
  | { t: 'PlayerSnapshot' }
  | { t: 'Remote'; action: RemoteAction; payload?: number }
  | { t: 'ProgramPick'; index: number }
  | { t: 'Tune'; live: boolean; manualIndex?: number }
  | { t: 'Error'; videoId: string }
  | { t: 'TimerFired'; id: TimerId }

/**
 * Pick the confirm button's spawn spot. The gag is that it runs from the
 * pointer (see adConfirmPointerMove) — so it must NOT appear under the pointer
 * that just clicked. Launch it toward the open middle of the screen, well past
 * the flee radius, so it's already out of reach the instant it appears. px/py
 * is the click point. Falls back to the farthest inset corner if clamping to
 * the viewport would pin it back within reach.
 */
function spawnAdConfirmAway(px: number, py: number): { left: number; top: number } {
  const maxLeft = Math.max(AD_CONFIRM_MARGIN, window.innerWidth - AD_CONFIRM_W - AD_CONFIRM_MARGIN)
  const maxTop = Math.max(AD_CONFIRM_MARGIN, window.innerHeight - AD_CONFIRM_H - AD_CONFIRM_MARGIN)
  const clampLeft = (l: number) => Math.min(maxLeft, Math.max(AD_CONFIRM_MARGIN, l))
  const clampTop = (t: number) => Math.min(maxTop, Math.max(AD_CONFIRM_MARGIN, t))
  const edgeDist = (left: number, top: number) => {
    const nx = Math.max(left, Math.min(px, left + AD_CONFIRM_W))
    const ny = Math.max(top, Math.min(py, top + AD_CONFIRM_H))
    return Math.hypot(nx - px, ny - py)
  }

  const toCx = window.innerWidth / 2 - px
  const toCy = window.innerHeight / 2 - py
  const d = Math.hypot(toCx, toCy) || 1
  const reach = AD_CONFIRM_FLEE_RADIUS + AD_CONFIRM_H + 48
  const left = clampLeft(px + (toCx / d) * reach - AD_CONFIRM_W / 2)
  const top = clampTop(py + (toCy / d) * reach - AD_CONFIRM_H / 2)
  if (edgeDist(left, top) >= AD_CONFIRM_FLEE_RADIUS) return { left, top }

  // Clamping pinned it back into reach — bail to the farthest inset corner.
  const corners = [
    { left: AD_CONFIRM_MARGIN, top: AD_CONFIRM_MARGIN },
    { left: maxLeft, top: AD_CONFIRM_MARGIN },
    { left: AD_CONFIRM_MARGIN, top: maxTop },
    { left: maxLeft, top: maxTop },
  ]
  return corners.reduce((best, c) =>
    edgeDist(c.left, c.top) > edgeDist(best.left, best.top) ? c : best,
  )
}

const KEYMAP: Record<string, RemoteAction> = {
  p: 'power',
  P: 'power',
  ArrowUp: 'chUp',
  ArrowDown: 'chDown',
  ArrowLeft: 'progPrev',
  ArrowRight: 'progNext',
  '+': 'volUp',
  '=': 'volUp',
  '-': 'volDown',
  _: 'volDown',
  m: 'mute',
  M: 'mute',
  g: 'guide',
  G: 'guide',
  f: 'fullscreen',
  F: 'fullscreen',
  l: 'live',
  L: 'live',
  r: 'remote',
  R: 'remote',
  Enter: 'ok',
  Escape: 'back',
  ' ': 'play',
}

const clamp = (v: number, lo: number, hi: number) => Math.max(lo, Math.min(hi, v))
const pad = (n: number) => (n < 10 ? '0' : '') + n
/** True on a touch (coarse-pointer) device. Reads the media query DIRECTLY (not the body.is-touch
 *  class) so it's correct on the very first call — the class-setting effect runs only after commit,
 *  but applyRemotePos / drag guards run during the ref callback, before it. */
const isTouch = () => typeof window !== 'undefined' && !!window.matchMedia && window.matchMedia('(pointer: coarse)').matches
const offAirChannel = (num: number, base?: Channel): Channel => ({
  number: num,
  name: 'NO SIGNAL',
  slug: `off-air-${num}`,
  playlist: null,
  playlistId: null,
  videos: [],
  youtubeChannels: [],
  excludeVideoIds: [],
  genre: '',
  tagline: 'Please stand by',
  ident: base?.ident ?? '#1d4ed8',
  ink: base?.ink ?? '#dbe7ff',
  logo: '',
  bumper: base?.bumper ?? 'bars',
  schedule: 'OFF AIR',
  noSignal: true,
  kind: 'video',
  component: null,
  keywords: [],
  adReel: [],
  programs: [],
  origin: base?.origin ?? 'builtin',
  hidden: base?.hidden ?? false,
})

export function TvProvider({
  channels,
  children,
}: {
  channels: Channel[]
  children: ReactNode
}) {
  const machine = useMemo(() => createTvMachine(channels), [channels])
  const loggingReducer = useMemo(() => {
    // The runaway ad-confirm button dispatches its position every pointer frame
    // (~60/s) while being chased; logging those would bury the console and cost
    // real time. They carry no debugging value, so keep them quiet.
    const QUIET = new Set<TvAction['type']>(['AD_BUMPER_CONFIRM_SET', 'AD_BUMPER_CONFIRM_LABEL'])
    return (s: TvState, a: TvAction) => {
      const next = machine.reducer(s, a)
      if (!QUIET.has(a.type)) {
        console.log(`[TV] [Store Action]`, a.type, a, `=> State:`, {
          power: next.power,
          channelIndex: next.channelIndex,
          programIndex: next.programIndex,
          screen: next.screen,
          playback: next.playback,
          bumper: next.bumper,
          audio: next.audio,
          live: next.live,
          settings: next.settings,
        })
        Sentry.addBreadcrumb({
          category: 'tv.action',
          message: a.type,
          data: {
            action: a,
            state: {
              power: next.power,
              screen: next.screen,
              playback: next.playback,
              channelIndex: next.channelIndex,
              audio: next.audio,
              bumper: next.bumper,
            },
          },
          level: 'info',
        })
      }
      return next
    }
  }, [machine])
  const [state, dispatch] = useReducer(loggingReducer, channels, buildInitialState)
  const [tubeReady, setTubeReady] = useState(false)

  // Latest values for stale-closure-safe access inside imperative handlers.
  const stateRef = useRef(state)
  stateRef.current = state
  const tubeReadyRef = useRef(tubeReady)
  tubeReadyRef.current = tubeReady

  // All machinery (engines, DOM nodes, timers, flags) — never render state.
  const R = useRef({
    dual: null as DualPlayer | null,
    snow: null as Snow | null,
    tvNode: null as HTMLDivElement | null,
    screenNode: null as HTMLDivElement | null,
    playerHostA: null as HTMLDivElement | null,
    playerHostB: null as HTMLDivElement | null,
    snowNode: null as HTMLCanvasElement | null,
    nsBox: null as HTMLDivElement | null,
    remote: null as HTMLDivElement | null,
    bumpCanvas: null as HTMLCanvasElement | null,
    bumpOverlay: null as HTMLElement | null, // host for the HTML/CSS now-playing overlays
    bumpRunner: null as import('@/bumps').OverlayDirector | null,
    bumpLoading: false,
    sceneShowing: false, // the Stage's WebGL scene layer (cover / ad) is up
    cardShowing: false, // the now-playing DOM card is up — independent of the scene
    bumpChannel: null as Channel | null, // the channel the active effects were computed FOR — the scene
    // presenter builds its bump context from THIS (not current(), which lags a TUNE dispatch by a render)
    bumpPending: null as string | null, // bump name waiting on the lazy runner
    t: {
      bumper: 0,
      srcFb: 0,
      whack: 0,
      poll: 0,
      ns: 0,
      beam: 0,
      fsIdle: 0, // fullscreen remote auto-hide (5s)
      dial: 0, // multi-digit channel-entry debounce
      bugReport: 0, // report-bug feedback auto-clear (4s)
      osd: { channel: 0, banner: 0, volume: 0 } as Record<string, number>,
    },
    pendingTune: false,
    armed: false,
    silentUrl: null as string | null,
    lastBumper: null as string | null, // last ident shown (avoid back-to-back repeat)
    dialNum: 0, // digits accumulated during channel entry
    dialActive: false, // mid multi-digit channel entry
    // Intent to come up LIVE, carried across the audio-unlock gate (it must
    // survive the locked → whack → doUnlock gap, before state.live can be set).
    // state.live means "actually live now"; R.wantLive means "go live once we can".
    wantLive: false,
    // Wall-clock of the last time the player was genuinely following (PLAYING/
    // BUFFERING), so the reconciler can tolerate a brief spin-up blip without
    // flapping the live tag.
    lastFollowingAt: 0,
    // Wall-clock of the last drift-correcting seek; gates the SEEK_COOLDOWN_MS so we
    // don't re-seek before the previous one has settled (the live stutter loop).
    lastSeekAt: 0,
    // True-fullscreen uses the native Fullscreen API; this marks that WE entered it, so
    // the fullscreenchange listener only syncs state for our own fullscreen (incl. ESC).
    usingNativeFs: false,
    // Synchronous re-entrancy guard for doUnlock: state.audio lags the UNLOCK
    // dispatch, so without this two near-simultaneous callers (whack-watch + the
    // PLAYING event + the probe) each run the whole unlock+live-jump body. Reset
    // when a fresh gesture is armed (watchForWhack) and on power-off.
    unlocking: false,

    // The reconciler's queue, kept in a ref so it's fresh SYNCHRONOUSLY — the store
    // queue (SET_QUEUE) lags a render, but an intent-setter runs reconcile in the
    // same tick, so it must read the queue from here, not stateRef.
    queue: [] as QueueItem[],
    // ---- REACTIVE PLAYBACK INTENTS (the reconciler's desired inputs) ----
    // The non-live desired position (manual nav / resume). null while live —
    // computeDesired ignores it then. The executor advances its index when the
    // foreground video ENDs (non-live autoplay).
    manual: null as { index: number; atSec: number } | null,
    // Paused intent (play/pause toggle). desired → 'off' while true.
    paused: false,
    // Pure playback core state. During cutover this is the authority, while the legacy fields
    // below are mirrored for UI helpers that still read R.* directly.
    model: initialPlaybackModel(),
    // A channel TUNE in flight (null = settled). The masking channel cover is DERIVED from
    // this intent timeline (src/tv/cover.ts), NOT from buffer state — so the bumper is the
    // visible consequence of "I switched channels", independent of what's buffered. Re-stamped
    // on every channel landing (mash → land on the LAST channel); cleared once landed.
    tune: null as import('./cover').TuneIntent | null,
    // The IDENTITY of what the single BumpRunner overlay currently shows — e.g. `channel:<tune.at>`,
    // `ad:<index>`, `ident:<videoId>`, or null. The executor derives the DESIRED key each tick and
    // re-shows whenever it differs, so a new tune event / new video naturally restarts the scene
    // from scratch (no imperative "reset the cache" at call sites).
    shownKey: null as string | null,
    // The resolved ident scene — stored so the timer's maxMs and the show use the SAME random
    // pick — and the current desired queue index (for buildBumpCtx's "now playing" title).
    identItem: null as { scene: string; params: Record<string, unknown>; maxMs?: number } | null,
    desiredIndex: 0,
    // The fg video id we last auto-advanced past on ENDED, so a lingering ENDED
    // state can't double-skip before the swap lands.
    lastEndedId: null as string | null,
    // Video ids that failed to play (after the DualPlayer's retries) — the desired
    // frame skips over these so a dead video can't strand playback.
    deadIds: new Set<string>(),
    consecutiveErrors: 0, // distinct-video failures with no genuine play between → systemic block
    // Timing for a NON-LIVE ad (there's no video ENDED to advance on): when the manual
    // position first lands on an ad slot we stamp the start; once its scheduled length
    // elapses the executor advances to the next item (live ads advance by wall-clock).
    adStartedAt: 0,
    adStartedIndex: null as number | null,
    // LIVE CATCH-UP. Two orthogonal facts:
    //  - liveSynced: have we reached live at all since the last landing? false = ACQUIRE (seek to
    //    live — a landing must jump, not coast); true = TRACK (coast on stalls, reclaim at ads).
    //  - liveLag: seconds the picture is behind real-live (only meaningful while TRACK; 0 in
    //    ACQUIRE). Absorbed from stalls, reclaimed in ad tails (catchup.ts). The schedule
    //    (liveSlot) and the LIVE badge stay on real-live, so the badge reads the lag as
    //    off-liveness.
    liveSynced: false,
    liveLag: 0,
    lastReclaimedAdIndex: null as number | null, // once-per-ad guard for the reclaim
    // The program "now playing" IDENT: a transparent overlay shown over the playing video for
    // a few seconds of each new in-channel program. identActive is a TIMED flag (set on the
    // new-video edge, auto-cleared after the ident's maxMs); the overlay derivation shows the
    // ident when no masking cover is up. lastInterstitialVideo edge-guards "fire once per video".
    lastInterstitialVideo: null as string | null,
    interstitialTimer: 0,
    identActive: false,
  }).current

  // The controller is built once; it closes over stable refs + dispatch and
  // reads the freshest state via stateRef. Stable identity keeps the context
  // value cheap and avoids re-subscribing effects.
  const ctrl = useMemo(() => {
    const mirrorModelDispatch = (a: TvAction) => {
      switch (a.type) {
        case 'SCREEN':
          R.model.screen = a.mode
          break
        case 'SET_PLAYBACK':
          R.model.playback = a.phase
          break
        case 'SET_LIVE':
          R.model.live = a.on
          break
        case 'SET_SCREEN_INTERACTIVE':
          R.model.screenInteractive = a.on
          break
        case 'SET_BLOCKED':
          R.model.blocked = a.on
          break
      }
    }
    const D = (a: TvAction) => {
      mirrorModelDispatch(a)
      dispatch(a)
    }
    // Set the queue everywhere at once: the store (for the LCD list, async) and the
    // reconciler's synchronous ref (so a same-tick reconcile sees it immediately).
    const pushQueue = (queue: QueueItem[]) => {
      R.queue = queue
      D({ type: 'SET_QUEUE', queue })
    }
    const current = () => {
      const offAirNumber = stateRef.current.offAirNumber
      if (offAirNumber != null) {
        return offAirChannel(offAirNumber, channels.find((ch) => ch.noSignal) ?? channels[0])
      }
      return channels[stateRef.current.channelIndex]
    }
    const indexOfNumber = machine.indexOfNumber

    /* ---- global theming / glow (CSS custom props on :root) ---- */
    const root = document.documentElement.style
    const setTheme = (ch: Channel) => {
      root.setProperty('--ident', ch.ident)
      root.setProperty('--ink', ch.ink)
    }
    const setGlow = (v: number) => root.setProperty('--glow', String(v))

    /* ---- ephemeral CSS-class animations (drive registered nodes) ---- */
    const flashClass = (node: HTMLElement | null, cls: string, ms: number) => {
      if (!node) return
      node.classList.remove(cls)
      void node.offsetWidth // restart the animation
      node.classList.add(cls)
      window.setTimeout(() => node.classList.remove(cls), ms)
    }
    const playWarmup = () => flashClass(R.screenNode, screenStyles.playWarmup, 1050)
    const playCollapse = () => flashClass(R.screenNode, screenStyles.playCollapse, 560)
    const whackShake = () => flashClass(R.tvNode, cabinetStyles.whacking, 520)

    /* ---- OSD overlays ---- */
    const flashOsd = (kind: 'channel' | 'banner' | 'volume', holdMs?: number) => {
      if (kind === 'channel') D({ type: 'OSD_OVERRIDE', value: null })
      D({ type: 'OSD_SHOW', kind })
      window.clearTimeout(R.t.osd[kind])
      R.t.osd[kind] = window.setTimeout(
        () => D({ type: 'OSD_HIDE', kind }),
        holdMs ?? (kind === 'volume' ? 1500 : 3200),
      )
    }

    const setScreen = (mode: TvState['screen']) => D({ type: 'SCREEN', mode })

    /* ---- ident selection ----
       Picks the IDENT scene to cover a transition: 'main' for a channel tune,
       'program' for an inter-video cover. (Ads are no longer resolved here — they
       are baked into the queue by injectAds and rendered as content by the
       playback FSM.) lastBumper avoids showing the same ident twice running. */
    const resolveBreak = (ch: Channel, slotKind: 'main' | 'program', transitionReason: 'tune' | 'next' | 'jump') => {
      const { item, nextState } = pureResolveBreak(
        ch,
        slotKind,
        transitionReason,
        { lastBumper: R.lastBumper },
        BUMPER_CHANNEL_MS,
        BUMPER_PROGRAM_MS,
      )
      R.lastBumper = nextState.lastBumper
      return item
    }
    /* ---- PLAYBACK CORE (pure update + effect interpreter) ---- */
    const engineEnv: EngineEnv = {
      resolveIdent: (channel, lastBumper) => {
        const { item, nextState } = pureResolveBreak(channel, 'program', 'next', { lastBumper }, BUMPER_CHANNEL_MS, BUMPER_PROGRAM_MS)
        return { item: item ? { scene: item.scene, params: item.params, maxMs: item.maxMs } : null, nextLastBumper: nextState.lastBumper }
      },
      resolveChannelBump: (channel, lastBumper) => {
        const { item, nextState } = pureResolveBreak(channel, 'main', 'tune', { lastBumper }, BUMPER_CHANNEL_MS, BUMPER_PROGRAM_MS)
        const it = item ?? { scene: channel.bumper || 'sweep', params: {} }
        return { scene: it.scene, params: it.params, nextLastBumper: nextState.lastBumper }
      },
    }
    const syncLegacyFromModel = (model: PlaybackModel) => {
      const next = { ...model, deadIds: new Set(model.deadIds) }
      R.model = next
      R.wantLive = next.wantLive
      R.manual = next.manual
      R.paused = next.paused
      R.liveSynced = next.liveSynced
      R.liveLag = next.liveLag
      R.lastReclaimedAdIndex = next.lastReclaimedAdIndex
      R.lastFollowingAt = next.lastFollowingAt
      R.lastSeekAt = next.lastSeekAt
      R.tune = next.tune
      R.desiredIndex = next.desiredIndex
      R.identActive = next.identActive
      R.identItem = next.identItem
      R.lastInterstitialVideo = next.lastInterstitialVideo
      R.lastBumper = next.lastBumper
      R.shownKey = next.shownKey
      R.adStartedAt = next.adStartedAt
      R.adStartedIndex = next.adStartedIndex
      R.lastEndedId = next.lastEndedId
      R.deadIds = new Set(next.deadIds)
      R.consecutiveErrors = next.consecutiveErrors
    }
    const eventContext = (chOverride?: Channel, nowOverride?: number): EventContext | null => {
      const dual = R.dual
      if (!dual) return null
      return {
        now: nowOverride ?? Date.now(),
        queue: R.queue,
        channel: chOverride ?? current(),
        snapshot: dual.snapshot(),
        playerIndex: dual.getIndex(),
        shuffle: stateRef.current.shuffle,
        adBumpers: stateRef.current.settings.adBumpers,
      }
    }
    const clearEngineTimer = (id: TimerId) => {
      if (id !== 'ident') return
      if (R.interstitialTimer) window.clearTimeout(R.interstitialTimer)
      R.interstitialTimer = 0
    }
    const applyEngineEffects = (effects: Effect[], ch: Channel) => {
      const dual = R.dual
      if (!dual) return
      R.bumpChannel = ch // the scene presenter (called synchronously below) reads this for its context
      for (const effect of effects) {
        switch (effect.e) {
          case 'AdoptQueue':
            dual.adoptQueue(effect.ids, effect.index)
            break
          case 'StageBackground':
            dual.ensureBackground(effect.id, effect.atSec ?? 0)
            break
          case 'Swap':
            dual.swapToBackground(effect.index, effect.seekSec)
            break
          case 'SeekForeground':
            dual.seekForeground(effect.sec)
            break
          case 'PlayForeground':
            dual.playForeground()
            break
          case 'PauseForeground':
            dual.pause()
            break
          case 'Cover':
            dual.setCovered(effect.on)
            break
          case 'SetIndex':
            dual.setIndex(effect.index)
            break
          // --- Stage: the full-frame content presentable (the Stage owns it + tears it down) ---
          case 'PresentScene':
            dual.presentScene(effect.scene, effect.params, effect.startSec)
            break
          case 'PresentComponent':
            dual.presentComponent(effect.component)
            break
          case 'RevealVideo':
            dual.revealVideo()
            break
          case 'BlankStage':
            dual.blankStage()
            break
          // --- the lone overlay: the now-playing ident card (DOM, over the active presentable) ---
          case 'ShowOverlay':
            showCardBump(ch, { scene: effect.scene, params: effect.params })
            break
          case 'HideOverlay':
            hideCardBump()
            break
          case 'ScheduleTimer':
            clearEngineTimer(effect.id)
            if (effect.id === 'ident') {
              R.interstitialTimer = window.setTimeout(() => {
                R.interstitialTimer = 0
                applyPlaybackEvent({ t: 'TimerFired', id: effect.id })
              }, Math.max(0, effect.ms))
            }
            break
          case 'CancelTimer':
            clearEngineTimer(effect.id)
            break
          case 'SetScreen':
            setScreen(effect.mode)
            break
          case 'SetPlayback':
            D({ type: 'SET_PLAYBACK', phase: effect.phase })
            break
          case 'SetLive':
            D({ type: 'SET_LIVE', on: effect.on })
            break
          case 'SetScreenInteractive':
            D({ type: 'SET_SCREEN_INTERACTIVE', on: effect.on })
            break
          case 'SetBlocked':
            D({ type: 'SET_BLOCKED', on: effect.on })
            break
          case 'SetProgram':
            D({ type: 'SET_PROGRAM', index: effect.index })
            break
          case 'Log':
            console.log(`[TV] [Engine] ${effect.message}`, effect.data ?? {})
            Sentry.addBreadcrumb({
              category: 'tv.engine',
              message: effect.message,
              data: effect.data,
              level: 'info',
            })
            break
          case 'Audio':
            break
        }
      }
    }
    const applyPlaybackEvent = (
      seed: PlaybackEventSeed,
      chOverride?: Channel,
      nowOverride?: number,
    ) => {
      const s = stateRef.current
      if (!R.dual || s.power === 'off') return
      if (s.audio !== 'unlocked' && !R.unlocking) return
      const ctx = eventContext(chOverride, nowOverride)
      if (!ctx) return
      const event = { ...seed, ...ctx } as PlaybackEvent
      const { model, effects } = engineUpdate(R.model, event, engineEnv)
      syncLegacyFromModel(model)
      applyEngineEffects(effects, ctx.channel)
    }

    /* ---- bump scenes (lazy Three.js runner, store-owned) ---- */
    const buildBumpCtx = (ch: Channel): import('@/bumps').BumpContext => {
      const s = stateRef.current
      // The ident announces the CURRENT desired program (the one we're landing on), tracked
      // each reconcile tick — `R.dual.getIndex()` lags the swap, so it'd show the old one.
      const idx = R.desiredIndex ?? (R.dual ? R.dual.getIndex() : s.programIndex)
      const upcomingItem = s.queue[idx]
      const upcomingTitle = upcomingItem?.title
      return {
        channel: {
          number: ch.number,
          name: ch.name,
          slug: ch.slug,
          tagline: ch.tagline,
          genre: ch.genre,
          ident: ch.ident,
          ink: ch.ink,
          schedule: ch.schedule,
        },
        now: upcomingTitle ? { title: upcomingTitle } : null,
        next: null,
        clock: pad(new Date().getHours()) + ':' + pad(new Date().getMinutes()),
        params: {},
      }
    }
    const ensureBumpRunner = () => {
      if (R.bumpRunner || R.bumpLoading || !R.bumpCanvas || !R.bumpOverlay) return
      R.bumpLoading = true
      import('@/bumps')
        .then(({ createOverlayDirector }) => {
          R.bumpLoading = false
          if (!R.bumpCanvas || !R.bumpOverlay) return
          R.bumpRunner = createOverlayDirector(R.bumpCanvas, R.bumpOverlay)
          if (R.bumpPending) {
            R.bumpRunner.show(R.bumpPending, buildBumpCtx(current()))
            R.bumpPending = null
          }
        })
        .catch(() => {
          R.bumpLoading = false
        })
    }
    const bumpCtxFor = (ch: Channel, params: Record<string, unknown>) => {
      const ctx = buildBumpCtx(ch)
      ctx.params = params
      return ctx
    }
    // state.bumper (the .bumper wrapper opacity) is visible while EITHER layer is up; tracking them
    // independently (not one flag) keeps a card-hide that races a scene-show from blanking the scene.
    const syncBumperVisible = () => D({ type: R.sceneShowing || R.cardShowing ? 'BUMPER_SHOW' : 'BUMPER_HIDE' })

    // SCENE layer — the Stage's full-frame content presentable (cover / ad / ad-channel). WebGL.
    const showSceneBump = (
      ch: Channel,
      item: { scene: string; params: Record<string, unknown>; maxMs?: number; startSec?: number },
    ) => {
      R.sceneShowing = true
      syncBumperVisible()
      if (R.bumpRunner) return R.bumpRunner.show(item.scene, bumpCtxFor(ch, item.params), item.maxMs, item.startSec, false)
      R.bumpPending = item.scene
      ensureBumpRunner()
      return Promise.resolve()
    }
    const hideSceneBump = () => {
      R.sceneShowing = false
      R.bumpPending = null
      syncBumperVisible()
      R.bumpRunner?.hideScene()
    }
    // CARD layer — the now-playing program-card. DOM, composited over the video. Independent lifecycle.
    const showCardBump = (ch: Channel, item: { scene: string; params: Record<string, unknown> }) => {
      R.cardShowing = true
      syncBumperVisible()
      R.bumpRunner?.show(item.scene, bumpCtxFor(ch, item.params), undefined, 0, true)
    }
    const hideCardBump = () => {
      R.cardShowing = false
      syncBumperVisible()
      R.bumpRunner?.hideCard()
    }
    const bumpSync = () => {
      if (R.bumpRunner && (R.sceneShowing || R.cardShowing)) R.bumpRunner.setContext(buildBumpCtx(R.bumpChannel ?? current()))
    }

    // imperative cover (locked-path tune): the channel bumper is a SCENE the Stage shows.
    const showBumper = (
      ch: Channel,
      item?: { scene: string; params: Record<string, unknown>; maxMs?: number; startSec?: number; overlay?: boolean },
    ) => showSceneBump(ch, item || { scene: ch.bumper || 'sweep', params: {} })
    const hideBumper = () => {
      hideSceneBump()
      hideCardBump() // bring the now-playing CARD down too — it's a SEPARATE DOM layer nothing else clears,
      // so on a power-off / snow transition it would otherwise linger over the dead screen.
    }

    const wake = () => {
      TVAudio.resume()
      BumpAudio.unlock() // bump soundtracks ride the same first-gesture unlock as TVAudio
      R.armed = true
    }

    /* ---- polling: push live program into the store ---- */
    const stopPoll = () => {
      if (R.t.poll) window.clearInterval(R.t.poll)
      R.t.poll = 0
    }
    const startPoll = () => {
      stopPoll()
      // The reactive tick: the broadcast line moves by wall-clock, so re-run the
      // reconciler a few times a second to follow it (advance at boundaries, correct
      // drift, derive the live badge). Player-state events drive it too; this tick is
      // what covers the silent gaps between them.
      R.t.poll = window.setInterval(() => runReconcile(), 250)
    }

    /* ---- queue loading: SELF-DRIVEN playback (no YouTube playlist) ---- */
    // We advance the queue OURSELVES (DualPlayer), so there's no YT playlist and
    // no 200-item autoplay cap — the bug this app exists to fix. The queue is the
    // channel's embeddable baked programs (with titles for the LCD list), or the
    // explicit videos[] fallback, optionally shuffled.
    const channelQueue = (ch: Channel, shuffle = stateRef.current.shuffle): QueueItem[] =>
      buildChannelQueue(ch, {
        shuffle,
        adBumpersEnabled: stateRef.current.settings.adBumpers,
        // seed with the channel RNG so an UNSHUFFLED manual queue == the live/guide queue (the ad
        // split positions match what the guide shows; only shuffle deliberately diverges).
        random: channelRandom(ch),
      })

    const currentVideoOrder = (ch: Channel, currentId?: string): QueueItem[] => {
      const videos = stateRef.current.queue.filter(
        (q): q is Extract<QueueItem, { kind: 'video' }> => q.kind === 'video',
      )
      const ordered = videos.length ? videos : channelQueue(ch, false).filter((q) => q.kind === 'video')
      if (!currentId) return ordered
      const head = ordered.find((q) => q.videoId === currentId)
      const rest = ordered.filter((q) => q.videoId !== currentId)
      return head ? [head, ...rest] : ordered
    }

    const rebaseCurrentQueue = (queue: QueueItem[], currentId?: string) => {
      pushQueue(queue)
      const dual = R.dual
      if (!dual) return
      if (currentId) dual.rebaseQueue(queueEntries(queue))
      else if (stateRef.current.audio === 'unlocked') dual.setQueue(queueEntries(queue), 0, true)
      else dual.prepareQueue(queueEntries(queue), 0)
      // The pinned current video keeps playing at index 0 — point the manual intent
      // there (untracked) so the reconciler maintains it instead of going to 'off'.
      syncLegacyFromModel({
        ...R.model,
        paused: false,
        manual: { index: 0, atSec: 0 },
        wantLive: false,
      })
      checkReadyWithQueue()
    }
    // Build the queue, push it to the store (for the LCD list), and hand the ids
    // to the DualPlayer. `videoOrdinal` is which VIDEO to start on (0-based over
    // videos only — how the guide/tune/boot-resume think); we map it to the mixed
    // queue index so an injected ad never shifts the target. Prepared queues are
    // cued muted; the reconciler's Swap (swapToBackground) reveals at the bumper boundary.
    const loadChannelQueue = (ch: Channel, videoOrdinal: number, play: boolean) => {
      const dual = R.dual
      if (!dual) return
      const queue = channelQueue(ch)
      pushQueue(queue)
      const ids = queueEntries(queue)
      const startIndex = programOrdinalToQueueIndex(queue, videoOrdinal)
      if (play) dual.setQueue(ids, startIndex, true)
      else dual.prepareQueue(ids, startIndex)
      checkReadyWithQueue()
    }
    // Cue the channel muted (the locked, holding state — whack starts it).
    const cueSource = (ch: Channel, index = 0) => {
      const dual = R.dual
      if (!dual) return
      dual.mute()
      dual.setVolume(stateRef.current.volume)
      dual.setSubtitlesEnabled(stateRef.current.subtitles)
      loadChannelQueue(ch, index, false)
    }
    const cueResume = () => {
      const t = stateRef.current.bootResume
      if (!t) return
      const ch = channels[t.channelIndex]
      if (ch) cueSource(ch, t.programIndex || 0)
    }

    // Reset the program-ident overlay: cancel its hide-timer + flag + the resolved scene, and
    // forget the last-announced video (so a re-tune re-announces).
    const clearInterstitial = () => {
      if (R.interstitialTimer) window.clearTimeout(R.interstitialTimer)
      R.interstitialTimer = 0
      R.identActive = false
      R.identItem = null
      R.lastInterstitialVideo = null
      syncLegacyFromModel({
        ...R.model,
        identActive: false,
        identItem: null,
        lastInterstitialVideo: null,
      })
    }

    const cancelTransition = () => {
      // Abandon a pending no-signal snow-wash + the ident overlay. The CHANNEL cover is owned
      // by the R.tune timeline (stamped by landLive/landChannelManual, cleared on noSignal /
      // power-off), not here — so this never fights an in-flight bump.
      window.clearTimeout(R.t.bumper)
      R.t.bumper = 0
      clearInterstitial()
    }

    // Ready + queued → run the reconciler (lands a deferred boot once the tube warms).
    const checkReadyWithQueue = () => {
      if (tubeReadyRef.current && R.dual && R.dual.hasQueue()) runReconcile()
    }


    // Land on the live broadcast — purely by INTENT. Adopt the live queue, set the
    // intents (wantLive, no manual, not paused), and let the reactive reconciler do
    // the rest: it derives the cover (channel bumper when leaving a channel, program
    // ident / ad scene otherwise), pre-buffers the airing item in the background, and
    // swaps it in seeked to the KNOWN broadcast offset. No FSM, no masked-play, no
    // stale tune-start slot — the slot is recomputed every reconcile from the wall
    // clock. If the tube is still cold, the intent persists and onDualReady's
    // reconcile lands it. Returns false only when there's no computable schedule.
    const landLive = (ch: Channel, opts: { bump?: boolean } = {}): boolean => {
      if (!R.dual || ch.noSignal) return false
      const queue = buildLiveQueue(ch, stateRef.current.settings.adBumpers)
      if (!isLiveCapable(queue)) return false
      pushQueue(queue)
      cancelTransition()
      setGlow(0.55)
      startPoll()
      applyPlaybackEvent(opts.bump === false ? { t: 'Remote', action: 'live' } : { t: 'Tune', live: true }, ch)
      return true
    }
    // Land on a non-live position — adopt the channel queue and set the manual
    // intent at `videoOrdinal`. The reconciler covers the change (channel bumper
    // when leaving a channel) and swaps in the chosen program from its start.
    const landChannelManual = (ch: Channel, videoOrdinal: number) => {
      if (!R.dual || ch.noSignal) return
      const queue = channelQueue(ch)
      pushQueue(queue)
      const idx = programOrdinalToQueueIndex(queue, videoOrdinal)
      cancelTransition()
      setGlow(0.55)
      startPoll()
      applyPlaybackEvent({ t: 'Tune', live: false, manualIndex: idx }, ch)
    }

    /* ---- the whack: audio unlock detection + payoff ---- */
    const stopWhackWatch = () => {
      if (R.t.whack) window.clearInterval(R.t.whack)
      R.t.whack = 0
    }
    const watchForWhack = () => {
      stopWhackWatch()
      R.unlocking = false // arming a fresh gesture — allow the next doUnlock through
      R.t.whack = window.setInterval(() => {
        const s = stateRef.current
        if (s.audio !== 'awaiting-gesture') {
          stopWhackWatch()
          return
        }
        const ifr = R.dual?.getActiveIframe?.()
        if (ifr && document.activeElement === ifr) doUnlock()
      }, 200)
    }

    function doUnlock(silent?: boolean) {
      const s = stateRef.current
      if (s.audio === 'unlocked') return
      // Idempotency: state.audio only flips to 'unlocked' after the UNLOCK dispatch
      // re-renders, so guard synchronously against a second caller racing in first.
      if (R.unlocking) return
      R.unlocking = true
      stopWhackWatch()
      R.armed = true
      BumpAudio.unlock() // unlock bump soundtracks on every unlock path (idempotent)
      if (!silent) {
        whackShake()
        TVAudio.resume()
        TVAudio.whack()
      }
      TVAudio.stopHiss()
      try {
        window.focus()
      } catch {
        /* ignore */
      }

      const target = s.bootResume
      const wantLive = R.wantLive
      D({ type: 'UNLOCK' }) // adopts bootResume as the live channel
      const dual = R.dual
      dual?.setAudioUnlocked(true)

      // The channel we land on: the cued boot-resume target, else the current one.
      const ch = target ? channels[target.channelIndex] : current()
      setTheme(ch)
      flashOsd('channel')

      // Come up LIVE if that's the intent and the channel has a computable broadcast
      // schedule; otherwise resume the cued program as a manual position. Either is
      // pure intent — the reconciler covers the gap and reveals onto the landed item.
      // The intent survives a cold tube: onDualReady re-runs the reconciler.
      if (wantLive && dual && isLiveCapable(buildLiveQueue(ch, stateRef.current.settings.adBumpers))) {
        landLive(ch)
        return
      }

      if (target) {
        landChannelManual(ch, target.programIndex || 0)
        return
      }

      if (current() && dual) landChannelManual(ch, 0)
    }

    /* ---- tuning ---- */
    const tuneTo = (
      index: number,
      opts: { silent?: boolean; fromPower?: boolean; programIndex?: number; live?: boolean } = {},
    ) => {
      const n = channels.length
      const wrapped = ((index % n) + n) % n
      const ch = channels[wrapped]
      const startProgram = opts.programIndex ?? 0

      R.lastBumper = null
      syncLegacyFromModel({
        ...R.model,
        wantLive: !!opts.live, // record the intent so it survives the audio gate
        lastBumper: null,
      })

      D({ type: 'TUNE', index, programIndex: startProgram })
      setTheme(ch)
      flashOsd('channel')

      if (!opts.silent && !opts.fromPower) TVAudio.channelThunk()
      else if (opts.fromPower) TVAudio.staticBurst(0.2, 0.35)

      setScreen('tuning')
      R.snow?.start()
      TVAudio.stopHiss()
      cancelTransition()

      if (ch.noSignal) {
        syncLegacyFromModel({ ...R.model, tune: null, shownKey: null }) // dead frequency: snow, not a channel bumper
        hideBumper()
        D({ type: 'AWAIT_GESTURE', on: false })
        R.t.bumper = window.setTimeout(() => {
          setScreen('snow')
          setGlow(0.18)
          if (R.armed) TVAudio.startHiss(0.05)
          if (tubeReadyRef.current) R.dual?.stop()
        }, NO_SIGNAL_WASH_MS)
        stopPoll()
      } else if (stateRef.current.audio !== 'unlocked' && opts.live && isLiveCapable(buildLiveQueue(ch, stateRef.current.settings.adBumpers))) {
        // Locked but coming up LIVE (power-on): mirror the known-good boot-resume path
        // instead of forcing a whack. Do NOT pre-cue the stale persisted program — it flashed
        // a YouTube error / stale thumbnail and desynced the live offset, leaving the frame
        // stuck on snow. The power click IS a user gesture, so probe autoplay and unlock
        // silently; fall back to the whack CTA only if autoplay is genuinely blocked.
        setGlow(0.5)
        if (!tubeReadyRef.current) R.pendingTune = true
        watchForWhack()
        stopPoll()
        probeAudioAllowed().then((allowed) => {
          if (stateRef.current.audio === 'unlocked') return
          if (allowed) {
            doUnlock(true)
          } else {
            D({ type: 'AWAIT_GESTURE', on: true })
            const item = resolveBreak(ch, 'main', 'tune') || { scene: ch.bumper || 'sweep', params: {}, maxMs: BUMPER_CHANNEL_MS, weight: 1 }
            // Shown imperatively (reconcile is gated off while locked); on unlock the derived
            // overlay shows the real channel bump for the landed channel.
            showBumper(ch, item)
          }
        })
      } else if (stateRef.current.audio !== 'unlocked') {
        // Locked, non-live (manual) tune: cue the channel muted behind a channel bumper and
        // wait for the whack. doUnlock takes over (live land or resume) once audio is granted.
        D({ type: 'AWAIT_GESTURE', on: true })
        const item = resolveBreak(ch, 'main', 'tune') || { scene: ch.bumper || 'sweep', params: {}, maxMs: BUMPER_CHANNEL_MS, weight: 1 }
        showBumper(ch, item) // imperative while locked; derived overlay re-shows on unlock
        setGlow(0.5)
        if (tubeReadyRef.current) cueSource(ch, startProgram)
        else R.pendingTune = true
        watchForWhack()
        stopPoll()
      } else if (opts.live && isLiveCapable(buildLiveQueue(ch, stateRef.current.settings.adBumpers))) {
        // LIVE tune: pure intent. The reconciler covers the change and swaps in the
        // airing item seeked to the broadcast line — no FSM, no stale-slot race.
        landLive(ch)
      } else {
        // Non-live tune: land the chosen program; the reconciler covers it.
        landChannelManual(ch, startProgram)
      }
    }

    /* ---- power ---- */
    const togglePower = () => {
      wake()
      const turningOn = stateRef.current.power === 'off'
      D({ type: 'POWER_TOGGLE' })
      TVAudio.powerSwitch(turningOn)
      if (turningOn) {
        playWarmup()
        // Turning on resumes the LIVE broadcast on the last channel (default CH2).
        syncLegacyFromModel({ ...R.model, wantLive: true })
        window.setTimeout(() => tuneTo(stateRef.current.channelIndex, { fromPower: true, live: true }), 360)
      } else {
        R.snow?.hide() // fade the static out BEFORE the collapse so it doesn't snap to black under it
        playCollapse()
        TVAudio.stopHiss()
        stopPoll()
        cancelTransition()
        hideBumper()
        syncLegacyFromModel({
          ...initialPlaybackModel(),
          screen: 'off',
          playback: 'idle',
        }) // tear down the whole transition so a re-power-on is fresh
        R.armed = false // re-locked: a fresh power-on needs the gesture again
        R.unlocking = false
        D({ type: 'SET_LIVE', on: false })
        D({ type: 'AWAIT_GESTURE', on: false })
        D({ type: 'OSD_HIDE', kind: 'channel' })
        D({ type: 'OSD_HIDE', kind: 'banner' })
        D({ type: 'OSD_HIDE', kind: 'volume' })
        if (tubeReadyRef.current) R.dual?.stop()
        setGlow(0)
        window.setTimeout(() => setScreen('off'), 520)
      }
    }

    // Step to the next channel in the dial order, skipping hidden "secret"
    // channels the viewer hasn't discovered yet (those are reachable only by
    // typing their number). Discovered ones live in unlockedSecrets.
    const channelStep = (dir: 1 | -1) => {
      const s = stateRef.current
      const n = channels.length
      const unlocked = s.unlockedSecrets ?? []
      let idx = s.channelIndex
      for (let i = 0; i < n; i++) {
        idx = ((idx + dir) % n + n) % n
        const ch = channels[idx]
        if (!ch.hidden || unlocked.includes(ch.number)) break
      }
      // Live is sticky: surfing channels while live keeps you on each channel's
      // live broadcast. Only within-channel moves (prog +/-) drop you to on-demand.
      tuneTo(idx, { live: s.live })
    }

    /* ---- multi-digit channel entry (debounced, like a real TV) ----
       Press 6 then quickly 9 → the badge shows CH 06, then CH 69; after a short
       idle it turns green to confirm, then the set tunes. An unknown number is
       NOT bounced back to the nearest channel — it tunes to an empty frequency
       (snow + NO SIGNAL), which is what allows hidden / not-yet-added channels. */
    const DIAL_WAIT = 1200 // window to keep accepting more digits
    const DIAL_CONFIRM = 500 // green-flash beat before tuning
    const DIAL_MAX = 999 // 3-digit cap

    // Paint the dial badge with the digits so far (green once confirming).
    const renderDial = (confirm: boolean) => {
      const num = R.dialNum
      const found = indexOfNumber(num)
      const match = channels[found] && channels[found].number === num ? channels[found] : null
      D({ type: 'OSD_OVERRIDE', value: { num, label: match ? match.genre || match.name : '', confirm } })
      D({ type: 'OSD_SHOW', kind: 'channel' })
      window.clearTimeout(R.t.osd.channel) // the dial flow owns visibility, not auto-hide
    }

    const commitDial = () => {
      if (!R.dialActive) return
      renderDial(true) // confirm: number flips green
      window.clearTimeout(R.t.dial)
      R.t.dial = window.setTimeout(() => {
        const num = R.dialNum
        R.dialActive = false
        const found = indexOfNumber(num)
        if (channels[found] && channels[found].number === num) tuneTo(found, { live: stateRef.current.live })
        else tuneToDeadNumber(num)
      }, DIAL_CONFIRM)
    }

    const digit = (num?: number) => {
      if (num == null) return
      wake()
      const prev = R.dialActive ? R.dialNum : 0
      let next = prev * 10 + num
      if (next > DIAL_MAX) next = num // overflow → start a fresh number
      R.dialNum = next
      R.dialActive = true
      TVAudio.click({ freq: 1400, dur: 0.03 })
      renderDial(false)
      window.clearTimeout(R.t.dial)
      R.t.dial = window.setTimeout(commitDial, DIAL_WAIT)
    }

    // Tune to a number with no station: snow + NO SIGNAL, and STAY there.
    const tuneToDeadNumber = (num: number) => {
      D({ type: 'SET_OFF_AIR', num })
      flashOsd('channel') // override cleared → the badge reads offAirNumber
      TVAudio.channelThunk()
      setScreen('tuning')
      R.snow?.start()
      TVAudio.stopHiss()
      cancelTransition()
      hideBumper()
      syncLegacyFromModel({
        ...R.model,
        wantLive: false,
        manual: null,
        paused: false,
        tune: null,
        shownKey: null,
      })
      D({ type: 'AWAIT_GESTURE', on: false })
      R.t.bumper = window.setTimeout(() => {
        setScreen('snow')
        setGlow(0.18)
        if (R.armed) TVAudio.startHiss(0.05)
        if (tubeReadyRef.current) R.dual?.stop()
      }, NO_SIGNAL_WASH_MS)
      stopPoll()
    }

    /* ---- programs ---- */
    // Hand-steer the transport by moving the MANUAL intent; the reconciler swaps in
    // the pre-buffered next (instant) / loads prev on demand, covered by the ident.
    const programStep = (dir: 1 | -1) => {
      const ch = current()
      const dual = R.dual
      if (ch.noSignal || !tubeReadyRef.current || !dual) {
        TVAudio.staticBurst(0.15, 0.3)
        return
      }
      TVAudio.click({ freq: 1500, dur: 0.03 })
      TVAudio.staticBurst(0.14, 0.28)
      applyPlaybackEvent({ t: 'Remote', action: dir > 0 ? 'progNext' : 'progPrev' })
      flashOsd('banner')
    }

    const togglePlay = () => {
      const ch = current()
      const dual = R.dual
      if (ch.noSignal || !tubeReadyRef.current || !dual) return
      applyPlaybackEvent({ t: 'Remote', action: 'play' })
      flashOsd('banner')
    }

    // Jump to an explicit queue index (from the LCD video list) as the manual intent;
    // the reconciler covers it with the ident and swaps it in from the start.
    const playProgram = (index: number) => {
      const ch = current()
      if (ch.noSignal || !tubeReadyRef.current || stateRef.current.audio !== 'unlocked') return
      TVAudio.click({ freq: 1500, dur: 0.03 })
      applyPlaybackEvent({ t: 'ProgramPick', index })
      flashOsd('banner')
    }

    // LIVE button: land the current channel on its live broadcast position — pure
    // intent; the reconciler covers the jump with the ident/ad scene.
    const goLive = () => {
      const ch = current()
      if (ch.noSignal || !tubeReadyRef.current || stateRef.current.audio !== 'unlocked') return
      if (landLive(ch, { bump: false })) { // re-acquiring live on the CURRENT channel — no bumper
        TVAudio.click({ freq: 1500, dur: 0.03 })
      } else {
        // no enriched durations → can't compute a schedule; just flag it
        TVAudio.staticBurst(0.12, 0.25)
        flashOsd('banner')
      }
    }

    const runReconcile = (chOverride?: Channel, nowOverride?: number) => {
      applyPlaybackEvent({ t: 'Tick' }, chOverride, nowOverride)
    }
    const runPlayerSnapshot = () => {
      applyPlaybackEvent({ t: 'PlayerSnapshot' })
    }

    // Toggle shuffle for the current channel and rebuild its queue. Keeps the
    // currently-playing video at the front so playback isn't interrupted.
    const shuffleToggle = () => {
      // Shuffling breaks the broadcast schedule: drop the live intent and the derived
      // badge lapses (shuffle ⇒ not onBroadcast) on the next tick.
      syncLegacyFromModel({ ...R.model, wantLive: false })
      const on = !stateRef.current.shuffle
      D({ type: 'SET_SHUFFLE', on })
      const ch = current()
      if (ch.noSignal || !R.dual) {
        TVAudio.staticBurst(0.12, 0.25)
        return
      }
      TVAudio.click({ freq: 1200, dur: 0.04 })
      // Rebuild the queue under the new shuffle flag, pinning the current video
      // first so it keeps playing uninterrupted; rebaseQueue swaps the order and
      // re-buffers the standby without reloading the active frame.
      const cur = stateRef.current.queue[R.dual.getIndex()]
      const currentId = cur?.kind === 'video' ? cur.videoId : undefined
      let queue = channelQueue(ch, on)
      if (currentId) {
        const head = queue.find((q) => q.kind === 'video' && q.videoId === currentId)
        const rest = queue.filter((q) => !(q.kind === 'video' && q.videoId === currentId))
        queue = head ? [head, ...rest] : queue
      }
      pushQueue(queue)
      // rebaseQueue keeps the pinned current video playing uninterrupted at index 0
      // and re-buffers the standby. The manual intent points at it (untracked → no
      // seek), so the reconciler just maintains it and auto-advances on ENDED.
      R.dual.rebaseQueue(queueEntries(queue))
      syncLegacyFromModel({
        ...R.model,
        wantLive: false,
        paused: false,
        manual: { index: 0, atSec: 0 },
      })
      runReconcile(ch)
    }

    /* ---- volume ---- */
    const volumeStep = (dir: 1 | -1) => {
      const s = stateRef.current
      const newVol = clamp(s.volume + dir * VOL_STEP, 0, 100)
      D({ type: 'VOLUME_STEP', dir })
      if (tubeReadyRef.current && R.dual) {
        if (s.audio === 'unlocked') R.dual.unMute()
        R.dual.setVolume(newVol)
        R.dual.play()
      }
      TVAudio.volTick()
      TVAudio.setMuted(false)
      BumpAudio.setVolume(newVol / 100) // bump soundtracks track the TV volume (0..1)
      BumpAudio.setMuted(false)
      flashOsd('volume')
    }
    const muteToggle = () => {
      const s = stateRef.current
      const willMute = !s.muted
      D({ type: 'MUTE_TOGGLE' })
      if (tubeReadyRef.current && R.dual) {
        if (willMute) R.dual.mute()
        else if (s.audio === 'unlocked') {
          R.dual.unMute()
          R.dual.setVolume(s.volume)
          R.dual.play()
        }
      }
      TVAudio.click({ freq: 1300, dur: 0.03 })
      BumpAudio.setMuted(willMute) // mute tracks the TV mute
      flashOsd('volume')
    }

    /* ---- guide ---- */
    // The EPG header clock and the bump-scene clock both derive from the live
    // wall clock at render/show time, so the store holds no clock and there's no
    // ticking dispatch to manage here.
    const closeGuide = () => {
      D({ type: 'GUIDE_CLOSE' })
    }
    const toggleGuide = () => {
      if (stateRef.current.power === 'off') return
      D({ type: 'GUIDE_TOGGLE' })
      TVAudio.click({ freq: 1100, dur: 0.04 })
    }
    const guideMove = (dir: 1 | -1) => {
      D({ type: 'GUIDE_MOVE', dir, nowMs: Date.now() }) // nowMs lets the cursor land on the program airing now
      TVAudio.click({ freq: 1600, dur: 0.02, level: 0.15 })
    }
    // Guide open: prev/next move the program cursor within the SELECTED channel
    // — a preview only, no playback change.
    const guideProgramStep = (dir: 1 | -1) => {
      D({ type: 'GUIDE_PROGRAM_STEP', dir })
      TVAudio.click({ freq: 1300, dur: 0.02, level: 0.13 })
    }
    // Zoom the guide's visible time span (dir +1 = out/wider, -1 = in/narrower).
    const guideZoom = (dir: 1 | -1) => {
      D({ type: 'GUIDE_ZOOM', dir })
      TVAudio.click({ freq: dir < 0 ? 1700 : 1100, dur: 0.025, level: 0.14 })
    }
    // Go live on a channel: in-place if it's already the tuned one (program-card
    // cover), otherwise a full channel tune into the live broadcast position.
    const goLiveOn = (i: number) => {
      if (i === stateRef.current.channelIndex) goLive()
      else tuneTo(i, { live: true })
    }

    // Play a guide block: a future program plays from its start; the now-airing
    // block goes LIVE (synced to the broadcast). Closes the guide.
    const guidePlayBlock = (i: number, videoOrdinal: number, live: boolean) => {
      D({ type: 'GUIDE_SELECT', index: i })
      if (live) goLiveOn(i)
      else if (i === stateRef.current.channelIndex) {
        const ch = channels[i]
        const queue = channelQueue(ch)
        pushQueue(queue)
        const idx = programOrdinalToQueueIndex(queue, Math.max(0, videoOrdinal))
        applyPlaybackEvent({ t: 'ProgramPick', index: idx }, ch)
      } else {
        tuneTo(i, { programIndex: Math.max(0, videoOrdinal) })
      }
      window.setTimeout(closeGuide, 220)
    }
    const guidePlayChannelLive = (i: number) => guidePlayBlock(i, -1, true)

    // Guide Play/OK: tune to the selected (channel, program). If the cursor is on
    // the now-airing block we go live; otherwise we play it from the start.
    const guideTuneToSelected = () => {
      const { guideSel, guideProgram } = stateRef.current
      const ch = channels[guideSel]
      let isNow = false
      if (ch && !ch.noSignal) {
        const lq = buildLiveQueue(ch, stateRef.current.settings.adBumpers)
        const slot = liveSlot(lq, Date.now())
        if (slot) isNow = guideProgram === queueIndexToProgramOrdinal(lq, slot.index)
      }
      guidePlayBlock(guideSel, guideProgram, isNow)
    }
    const guideSelectAndTune = (index: number) => guidePlayChannelLive(index)

    // Guide search: set the live query (also activates search mode).
    const guideSearchSet = (text: string) => D({ type: 'GUIDE_SEARCH_SET', text })

    // Touch: collapse / expand the remote's LCD touchscreen panel.
    const remoteCollapse = () => D({ type: 'REMOTE_COLLAPSE' })

    // Guide Play/OK while searching: resolve the highlighted result and tune to it. A channel result
    // goes live (its TUNE unlocks a secret channel); a program result plays from its start unless it's
    // the now-airing block (then live), matching guideTuneToSelected.
    const guidePlaySearch = () => {
      const s = stateRef.current
      const results = searchGuide(channels, s.unlockedSecrets ?? [], s.guideSearch ?? '')
      if (!results.length) return
      const r = results[Math.min(Math.max(0, s.guideSearchSel), results.length - 1)]
      D({ type: 'GUIDE_SEARCH_CLOSE' })
      if (r.kind === 'channel') { guidePlayChannelLive(r.channelIndex); return }
      const ch = channels[r.channelIndex]
      let isNow = false
      if (ch && !ch.noSignal) {
        const lq = buildLiveQueue(ch, stateRef.current.settings.adBumpers)
        const slot = liveSlot(lq, Date.now())
        if (slot) isNow = r.ordinal === queueIndexToProgramOrdinal(lq, slot.index)
      }
      guidePlayBlock(r.channelIndex, r.ordinal, isNow)
    }

    /* ---- remote IR beam + key-press visual ---- */
    const beam = () => {
      const node = R.remote
      if (!node) return
      node.classList.add(remoteStyles.beam)
      window.clearTimeout(R.t.beam)
      R.t.beam = window.setTimeout(() => node.classList.remove(remoteStyles.beam), 150)
    }
    const pressVisual = (action: RemoteAction, payload?: number) => {
      const node = R.remote
      if (!node) return
      const sel =
        action === 'digit' ? `[data-digit="${payload}"]` : `[data-act="${action}"]`
      const btn = node.querySelector<HTMLElement>(sel)
      if (!btn) return
      const pressedClass = action === 'digit' ? touchStyles.pressed : remoteStyles.pressed
      btn.classList.add(pressedClass)
      window.setTimeout(() => btn.classList.remove(pressedClass), 130)
    }

    /* ---- the gated dispatch (remote + keyboard) ---- */
    const act = (action: RemoteAction, payload?: number) => {
      // Power is the one control that works while locked — it's how you turn the
      // set on from cold. Every other control needs the audio gate open.
      if (action !== 'power' && stateRef.current.audio !== 'unlocked') return
      R.armed = true
      wake()
      beam()
      pressVisual(action, payload)
      const guideOpen = stateRef.current.guideOpen
      switch (action) {
        case 'power':
          togglePower()
          break
        case 'chUp':
          if (guideOpen) guideMove(-1)
          else channelStep(1)
          break
        case 'chDown':
          if (guideOpen) guideMove(1)
          else channelStep(-1)
          break
        case 'volUp':
          if (guideOpen) guideZoom(-1) // in the guide, VOL +/- zooms the timeline
          else volumeStep(1)
          break
        case 'volDown':
          if (guideOpen) guideZoom(1)
          else volumeStep(-1)
          break
        case 'progPrev':
          if (guideOpen) guideProgramStep(-1)
          else programStep(-1)
          break
        case 'progNext':
          if (guideOpen) guideProgramStep(1)
          else programStep(1)
          break
        case 'play':
          if (guideOpen && stateRef.current.guideSearch !== null) guidePlaySearch()
          else if (guideOpen) guideTuneToSelected()
          else togglePlay()
          break
        case 'mute':
          muteToggle()
          break
        case 'guide':
          toggleGuide()
          break
        case 'ok':
          if (guideOpen && stateRef.current.guideSearch !== null) guidePlaySearch()
          else if (guideOpen) guideTuneToSelected()
          else togglePlay()
          break
        case 'back':
          if (guideOpen && stateRef.current.guideSearch !== null) D({ type: 'GUIDE_SEARCH_CLOSE' })
          else if (guideOpen) closeGuide()
          else if (stateRef.current.fullscreen) toggleFullscreen()
          break
        case 'digit':
          digit(payload)
          break
        case 'fullscreen':
          toggleFullscreen()
          break
        case 'live':
          if (!guideOpen) goLive()
          break
        case 'remote':
          // Touch: toggle the slide-up sheet (its home in BOTH windowed and fullscreen — the handle
          // summons it, this dismisses). Desktop: the fullscreen dock summon below.
          if (isTouch()) { D({ type: 'REMOTE_SHEET' }); break }
          // Summon / dismiss the on-screen remote — the only way to reach it in fullscreen on a
          // keyboard/TV (no hover-to-peek). Windowed, the remote is always visible, so this no-ops.
          if (stateRef.current.fullscreen) {
            if (stateRef.current.remoteFs === 'shown') {
              D({ type: 'REMOTE_FS', level: 'hidden' }); clearFsIdle()
              R.screenNode?.focus() // keep keyboard on our DOM after dismissing
            } else {
              D({ type: 'REMOTE_FS', level: 'shown' }); armFsIdle()
              requestAnimationFrame(() => R.remote?.focus()) // focus the remote so 'r' can dismiss it
            }
          }
          break
      }
    }

    const whackScreen = () => {
      // Locked: the click passes through our pointer-events:none overlays to the
      // iframe; detection (onStateChange / focus / blur) handles the unlock.
      // Unlocked AND settled into playback: a tap toggles play. The phase guard is
      // load-bearing — without it a rapid second tap during the unlock→live-cue
      // handoff (phases idle/tuning/bumping/ad) would toggle-pause the cue mid
      // establish and strand us off the wrong frame.
      const s = stateRef.current
      if (s.audio === 'unlocked' && s.playback === 'playing') togglePlay()
    }

    /* ---- remote drag ---- */
    // Grab the remote from ANYWHERE on its chassis — not just the top strip. We skip
    // interactive targets (buttons, the scrollable touchscreen) so they still click,
    // and only commit to a drag once the pointer actually moves past a small threshold,
    // so a press-and-release on the chassis can't swallow a click.
    const startRemoteDrag = (e: ReactPointerEvent) => {
      const node = R.remote
      if (!node) return
      if (isTouch()) return // on touch the remote is a bottom sheet, not a free-parked floater
      if (stateRef.current.fullscreen) return // docked by CSS in fullscreen; no drag
      if (e.button != null && e.button !== 0) return // left/primary only
      if ((e.target as HTMLElement).closest('button, a, input, select, textarea, [data-no-drag]')) return

      const r = node.getBoundingClientRect()
      const sx = e.clientX
      const sy = e.clientY
      const ox = r.left
      const oy = r.top
      let dragging = false
      const DRAG_THRESHOLD = 4

      const begin = () => {
        dragging = true
        node.classList.add(remoteStyles.dragging)
        node.style.left = ox + 'px'
        node.style.top = oy + 'px'
        node.style.right = 'auto'
        node.style.bottom = 'auto'
        // dragged ⇒ anchored top-left; keep the user scale but re-anchor its origin to match.
        node.style.transform = 'scale(var(--remote-scale, 1))'
        node.style.transformOrigin = 'top left'
      }
      const onMove = (ev: PointerEvent) => {
        if (!dragging) {
          if (Math.hypot(ev.clientX - sx, ev.clientY - sy) < DRAG_THRESHOLD) return
          begin()
        }
        // Free movement — no clamp; the remote can be parked anywhere, even partly off-screen.
        node.style.left = ox + (ev.clientX - sx) + 'px'
        node.style.top = oy + (ev.clientY - sy) + 'px'
      }
      const onUp = (ev: PointerEvent) => {
        window.removeEventListener('pointermove', onMove)
        window.removeEventListener('pointerup', onUp, true)
        if (!dragging) return // never moved → it was a click; leave it alone
        node.classList.remove(remoteStyles.dragging)
        // Suppress the click that would otherwise fire at drag-end.
        ev.preventDefault()
        D({
          type: 'SET_REMOTE_POS',
          pos: { left: parseFloat(node.style.left), top: parseFloat(node.style.top) },
        })
      }
      window.addEventListener('pointermove', onMove)
      window.addEventListener('pointerup', onUp, true)
    }

    const applyRemotePos = () => {
      const node = R.remote
      if (!node) return
      if (isTouch()) { // the sheet owns position on touch — clear any persisted floating coords
        node.style.left = node.style.top = node.style.right = node.style.bottom = node.style.transform = node.style.transformOrigin = ''
        return
      }
      const pos = stateRef.current.remotePos
      if (!pos) return
      node.style.left = pos.left + 'px'
      node.style.top = pos.top + 'px'
      node.style.right = 'auto'
      node.style.bottom = 'auto'
      node.style.transform = 'scale(var(--remote-scale, 1))'
      node.style.transformOrigin = 'top left'
    }

    /* ---- fullscreen: graceful zoom of the set into the window ---- */
    // Transform the whole .tv so the SCREEN fills the window (contain). We scale
    // about the screen's centre and translate that centre to the window centre,
    // measuring natural geometry first so the math is transform-independent.
    const FS_ZOOM_MS = 550
    // The IN-WINDOW zoom: scale `.tv` so the SCREEN fills the window (contain). Used for
    // ordinary fullscreen. TRUE fullscreen does NOT use this — it goes through the native
    // Fullscreen API (toggleFullscreen) so it can escape the cabinet's transforms.
    const applyFsTransform = (on: boolean, animate = true) => {
      const tv = R.tvNode
      const screen = R.screenNode
      if (!tv || !screen) return
      if (!on) {
        tv.style.transition = animate ? 'transform .5s cubic-bezier(.4,0,.2,1)' : 'none'
        tv.style.transform = ''
        tv.style.transformOrigin = ''
        return
      }
      // measure untransformed layout
      tv.style.transition = 'none'
      tv.style.transform = ''
      const sr = screen.getBoundingClientRect()
      const tvr = tv.getBoundingClientRect()
      const scale = Math.min(window.innerWidth / sr.width, window.innerHeight / sr.height)
      const ox = sr.left + sr.width / 2 - tvr.left
      const oy = sr.top + sr.height / 2 - tvr.top
      const tx = window.innerWidth / 2 - (sr.left + sr.width / 2)
      const ty = window.innerHeight / 2 - (sr.top + sr.height / 2)
      tv.style.transformOrigin = `${ox}px ${oy}px`
      const setTarget = () => {
        tv.style.transition = animate ? `transform ${FS_ZOOM_MS}ms cubic-bezier(.2,.8,.2,1)` : 'none'
        tv.style.transform = `translate(${tx}px, ${ty}px) scale(${scale})`
      }
      void tv.offsetWidth // commit the cleared state so the anim starts from identity
      if (animate) requestAnimationFrame(setTarget)
      else setTarget()
    }

    const clearFsIdle = () => {
      if (R.t.fsIdle) window.clearTimeout(R.t.fsIdle)
      R.t.fsIdle = 0
    }
    // Auto-hide the in-window remote after 5s of no interaction (incl. hover).
    const armFsIdle = () => {
      clearFsIdle()
      R.t.fsIdle = window.setTimeout(() => D({ type: 'REMOTE_FS', level: 'hidden' }), 5000)
    }

    const toggleFullscreen = () => {
      const goingOn = !stateRef.current.fullscreen
      const screen = R.screenNode

      // TRUE fullscreen → the native Fullscreen API on the SCREEN element. The browser
      // promotes it to the top layer, which is rendered outside the cabinet's transform/
      // filter hierarchy — so no ancestor can shrink it (a CSS overlay can't escape a
      // transformed ancestor; this invariably can). No reparent ⇒ the iframe doesn't
      // reload. State is driven by the fullscreenchange listener (so ESC stays in sync);
      // the request needs THIS click's user gesture, so we can't defer it.
      if (stateRef.current.settings.trueFullscreen && screen && document.fullscreenEnabled) {
        if (goingOn) {
          // Clear any leftover inline drag styles so the CSS fullscreen DOCK (not a stale dragged
          // position/transform) governs the remote once it's portaled into the fullscreen element.
          const r = R.remote
          if (r) { r.style.left = r.style.top = r.style.right = r.style.bottom = ''; r.style.transform = r.style.transformOrigin = '' }
          R.usingNativeFs = true
          screen.requestFullscreen().catch(() => { R.usingNativeFs = false })
        } else if (document.fullscreenElement) {
          document.exitFullscreen().catch(() => {})
        } else {
          D({ type: 'FULLSCREEN_SET', on: false })
        }
        return
      }

      // Ordinary IN-WINDOW fullscreen (the zoom).
      D({ type: 'FULLSCREEN_SET', on: goingOn })
      const r = R.remote
      if (r) {
        r.style.left = r.style.top = r.style.right = r.style.bottom = ''
        r.style.transform = r.style.transformOrigin = ''
      }
      if (!goingOn) {
        clearFsIdle()
        applyRemotePos()
      }
      applyFsTransform(goingOn)
    }

    // Keep `state.fullscreen` in step with the NATIVE fullscreen (true-fs path), incl.
    // the user pressing ESC. Only acts while we're the ones using native fs.
    const syncNativeFullscreen = () => {
      // Always reflect the REAL native-fs state (so the remote knows to portal into the top layer),
      // robust across every entry/exit path (button, ESC, the trueFullscreen setting toggle).
      const nativeFs = document.fullscreenElement != null && document.fullscreenElement === R.screenNode
      if (nativeFs !== stateRef.current.nativeFs) {
        D({ type: 'SET_NATIVE_FS', on: nativeFs })
        // Pull keyboard focus onto OUR screen element — otherwise the cross-origin YouTube iframe
        // holds it and our document keydown listener never sees keys (no shortcuts work in true fs).
        if (nativeFs) requestAnimationFrame(() => R.screenNode?.focus())
      }
      if (!R.usingNativeFs) return
      const inFs = !!document.fullscreenElement
      if (!inFs) R.usingNativeFs = false
      if (inFs !== stateRef.current.fullscreen) D({ type: 'FULLSCREEN_SET', on: inFs })
    }

    // Pointer near the bottom edge → peek the remote, tracking the cursor's x so
    // it rises right where you are (not centred). Position is held once the
    // remote is fully shown (clicked in), so you can aim at its buttons.
    const followRemoteX = (x: number) => {
      const node = R.remote
      if (!node) return
      const half = node.offsetWidth / 2
      const cx = clamp(x, half + 8, window.innerWidth - half - 8)
      node.style.left = cx + 'px' // CSS translate(-50%, …) centres on this x
      node.style.right = 'auto'
    }
    const pointerNearBottom = (near: boolean, x: number) => {
      const s = stateRef.current
      if (!s.fullscreen || s.remoteFs === 'shown') return
      if (near) followRemoteX(x)
      D({ type: 'REMOTE_FS', level: near ? 'peek' : 'hidden' })
    }
    // Click the peeking remote → it enters the window and stays (5s idle hide).
    const remoteActivate = () => {
      if (!stateRef.current.fullscreen) return
      D({ type: 'REMOTE_FS', level: 'shown' })
      armFsIdle()
    }
    // Any interaction with the shown remote (incl. hover) resets the idle timer.
    const remoteKeepAlive = () => {
      const s = stateRef.current
      if (!s.fullscreen || s.remoteFs !== 'shown') return
      armFsIdle()
    }

    /* ---- remote LCD touchscreen + display settings ---- */
    const setTouchScreen = (view: TvState['touchScreen']) => {
      TVAudio.click({ freq: 1400, dur: 0.03, level: 0.18 })
      D({ type: 'TOUCHSCREEN', view })
    }
    const setRemoteScale = (dir: 1 | -1) => {
      TVAudio.click({ freq: 1400, dur: 0.03, level: 0.18 })
      D({ type: 'SET_REMOTE_SCALE', delta: dir * 0.1 })
    }
    const rebuildForAdBumperSetting = (nextAdBumpers: boolean) => {
      const s = stateRef.current
      const ch = current()
      if (ch.noSignal) return
      const cur = R.dual ? s.queue[R.dual.getIndex()] : s.queue[s.programIndex]
      const currentId = cur?.kind === 'video' ? cur.videoId : undefined
      const videos = currentVideoOrder(ch, currentId)
      rebaseCurrentQueue(buildQueueFromVideos(videos, ch, nextAdBumpers), currentId)
    }

    const toggleSetting = (
      key: import('./types').DisplaySettingKey,
      /** the click point, used to spawn the ad-confirm button clear of it. */
      pointer?: { left: number; top: number },
    ) => {
      const s = stateRef.current
      TVAudio.click({ freq: 1800, dur: 0.025, level: 0.16 })
      if (key === 'adBumpers' && s.settings.adBumpers) {
        const px = pointer?.left ?? window.innerWidth - AD_CONFIRM_W - 24
        const py = pointer?.top ?? window.innerHeight - AD_CONFIRM_H - 24
        const spawn = spawnAdConfirmAway(px, py)
        D({
          type: 'AD_BUMPER_CONFIRM_SET',
          confirm: {
            left: spawn.left,
            top: spawn.top,
            vx: 0,
            vy: 0,
            label: AD_CONFIRM_LABELS[0],
          },
        })
        return
      }
      D({ type: 'TOGGLE_SETTING', key })
      // Toggling true-fullscreen while already fullscreen switches modes in place: into
      // native fullscreen (top layer, edge-to-edge) or back to the zoomed-bezel view. We
      // stay "fullscreen" either way. s is pre-toggle, so the fresh value is its inverse.
      if (key === 'trueFullscreen') {
        if (!stateRef.current.fullscreen) return
        const nowTrue = !s.settings.trueFullscreen
        const screen = R.screenNode
        if (nowTrue && screen && document.fullscreenEnabled) {
          applyFsTransform(false, false) // shed the zoom; native fs takes over
          R.usingNativeFs = true
          screen.requestFullscreen().catch(() => { R.usingNativeFs = false })
        } else if (document.fullscreenElement) {
          R.usingNativeFs = false // exit native, then zoom once the layout settles
          document.exitFullscreen().then(() => applyFsTransform(true, false), () => applyFsTransform(true, false))
        } else {
          applyFsTransform(true, false)
        }
        return
      }
      if (key !== 'adBumpers') return
      D({ type: 'AD_BUMPER_CONFIRM_HIDE' })
      rebuildForAdBumperSetting(true)
    }

    const adConfirmPointerMove = (point: { clientX: number; clientY: number }) => {
      const confirm = stateRef.current.adBumperConfirm
      if (!confirm) return
      const px = point.clientX
      const py = point.clientY
      const cx = confirm.left + AD_CONFIRM_W / 2
      const cy = confirm.top + AD_CONFIRM_H / 2
      const dx = cx - px
      const dy = cy - py
      const nearestX = Math.max(confirm.left, Math.min(px, confirm.left + AD_CONFIRM_W))
      const nearestY = Math.max(confirm.top, Math.min(py, confirm.top + AD_CONFIRM_H))
      const edgeDist = Math.hypot(nearestX - px, nearestY - py)
      if (edgeDist > AD_CONFIRM_FLEE_RADIUS) return

      const dirDist = Math.max(1, Math.hypot(dx, dy))
      const closeness = (AD_CONFIRM_FLEE_RADIUS - edgeDist) / AD_CONFIRM_FLEE_RADIUS
      const impulse = AD_CONFIRM_ACCEL * (0.45 + closeness)
      let vx = confirm.vx * AD_CONFIRM_DAMPING + (dx / dirDist) * impulse
      let vy = confirm.vy * AD_CONFIRM_DAMPING + (dy / dirDist) * impulse
      const speed = Math.hypot(vx, vy)
      if (speed > AD_CONFIRM_MAX_SPEED) {
        vx = (vx / speed) * AD_CONFIRM_MAX_SPEED
        vy = (vy / speed) * AD_CONFIRM_MAX_SPEED
      }

      const maxLeft = Math.max(AD_CONFIRM_MARGIN, window.innerWidth - AD_CONFIRM_W - AD_CONFIRM_MARGIN)
      const maxTop = Math.max(AD_CONFIRM_MARGIN, window.innerHeight - AD_CONFIRM_H - AD_CONFIRM_MARGIN)
      let left = confirm.left + vx
      let top = confirm.top + vy
      if (left < AD_CONFIRM_MARGIN) {
        left = AD_CONFIRM_MARGIN
        vx = Math.abs(vx) * 0.55
      } else if (left > maxLeft) {
        left = maxLeft
        vx = -Math.abs(vx) * 0.55
      }
      if (top < AD_CONFIRM_MARGIN) {
        top = AD_CONFIRM_MARGIN
        vy = Math.abs(vy) * 0.55
      } else if (top > maxTop) {
        top = maxTop
        vy = -Math.abs(vy) * 0.55
      }

      D({ type: 'AD_BUMPER_CONFIRM_SET', confirm: { ...confirm, left, top, vx, vy } })
    }

    // Idle wander: while the button is on screen and the pointer ISN'T near it,
    // nudge it around with a gentle random drift so it never sits perfectly
    // still. The flee handler takes over the moment the pointer gets close.
    const adConfirmIdleTick = (pointer: { clientX: number; clientY: number } | null) => {
      const confirm = stateRef.current.adBumperConfirm
      if (!confirm) return
      if (pointer) {
        const nx = Math.max(confirm.left, Math.min(pointer.clientX, confirm.left + AD_CONFIRM_W))
        const ny = Math.max(confirm.top, Math.min(pointer.clientY, confirm.top + AD_CONFIRM_H))
        if (Math.hypot(nx - pointer.clientX, ny - pointer.clientY) <= AD_CONFIRM_FLEE_RADIUS) return
      }

      let vx = confirm.vx * AD_CONFIRM_IDLE_DAMPING + (Math.random() - 0.5) * AD_CONFIRM_IDLE_ACCEL
      let vy = confirm.vy * AD_CONFIRM_IDLE_DAMPING + (Math.random() - 0.5) * AD_CONFIRM_IDLE_ACCEL
      const speed = Math.hypot(vx, vy)
      if (speed > AD_CONFIRM_IDLE_MAX_SPEED) {
        vx = (vx / speed) * AD_CONFIRM_IDLE_MAX_SPEED
        vy = (vy / speed) * AD_CONFIRM_IDLE_MAX_SPEED
      }

      const maxLeft = Math.max(AD_CONFIRM_MARGIN, window.innerWidth - AD_CONFIRM_W - AD_CONFIRM_MARGIN)
      const maxTop = Math.max(AD_CONFIRM_MARGIN, window.innerHeight - AD_CONFIRM_H - AD_CONFIRM_MARGIN)
      let left = confirm.left + vx
      let top = confirm.top + vy
      if (left < AD_CONFIRM_MARGIN) { left = AD_CONFIRM_MARGIN; vx = Math.abs(vx) }
      else if (left > maxLeft) { left = maxLeft; vx = -Math.abs(vx) }
      if (top < AD_CONFIRM_MARGIN) { top = AD_CONFIRM_MARGIN; vy = Math.abs(vy) }
      else if (top > maxTop) { top = maxTop; vy = -Math.abs(vy) }

      D({ type: 'AD_BUMPER_CONFIRM_SET', confirm: { ...confirm, left, top, vx, vy } })
    }

    const confirmAdBumpersOff = () => {
      if (!stateRef.current.settings.adBumpers) {
        D({ type: 'AD_BUMPER_CONFIRM_HIDE' })
        return
      }
      TVAudio.click({ freq: 900, dur: 0.045, level: 0.18 })
      D({ type: 'AD_BUMPER_CONFIRM_HIDE' })
      D({ type: 'TOGGLE_SETTING', key: 'adBumpers' })
      // Ads off ⇒ not the broadcast schedule: drop the live intent; reconcileLive
      // derives live=false on its own.
      syncLegacyFromModel({ ...R.model, wantLive: false })
      rebuildForAdBumperSetting(false)
    }
    const toggleSubtitles = () => {
      const on = !stateRef.current.subtitles
      TVAudio.click({ freq: 1700, dur: 0.025, level: 0.16 })
      D({ type: 'SUBTITLES_TOGGLE' })
      R.dual?.setSubtitlesEnabled(on)
    }
    const dismissExtensionHint = () => {
      try {
        localStorage.setItem(EXTENSION_HINT_KEY, '1')
      } catch {
        /* storage unavailable; the in-memory dismiss still holds for this session */
      }
      D({ type: 'EXTENSION_HINT_DISMISS' })
    }
    // Report-bug: capture the live session to Sentry and copy the session id so the user can hand
    // it to support. The Sentry/clipboard side effects + the auto-clear timer belong in the store,
    // not the stateless options panel — it just calls this and renders state.bugReport.
    const reportBug = () => {
      Sentry.captureMessage('User reported bug', { level: 'info', tags: { session_id: SESSION_ID } })
      navigator.clipboard
        .writeText(SESSION_ID)
        .then(() => D({ type: 'BUG_REPORT_SET', status: 'copied' }))
        .catch(() => D({ type: 'BUG_REPORT_SET', status: 'nocopy' }))
      window.clearTimeout(R.t.bugReport)
      R.t.bugReport = window.setTimeout(() => D({ type: 'BUG_REPORT_SET', status: false }), 4000)
    }

    /* ---- no-signal caption bounce (DVD-style) ---- */
    const startNoSignalBounce = () => {
      let x = 30
      let y = 30
      let dx = 1.1
      let dy = 0.9
      const step = () => {
        const host = R.screenNode
        const box = R.nsBox
        if (host && box) {
          const hr = host.getBoundingClientRect()
          const br = box.getBoundingClientRect()
          const maxX = hr.width - br.width - 6
          const maxY = hr.height - br.height - 6
          x += dx
          y += dy
          if (x <= 0 || x >= maxX) {
            dx *= -1
            x = clamp(x, 0, maxX)
          }
          if (y <= 0 || y >= maxY) {
            dy *= -1
            y = clamp(y, 0, maxY)
          }
          box.style.transform = `translate(${x}px,${y}px)`
        }
        R.t.ns = window.requestAnimationFrame(step)
      }
      window.cancelAnimationFrame(R.t.ns)
      step()
    }

    /* ---- DualPlayer callbacks ---- */
    // Both frames ready. If a tune was waiting on the engine, run it now.
    const onDualReady = () => {
      setTubeReady(true)
      tubeReadyRef.current = true
      R.dual?.setVolume(stateRef.current.volume)
      R.dual?.setSubtitlesEnabled(stateRef.current.subtitles)
      const s = stateRef.current
      if (s.power === 'off' || !R.pendingTune) {
        checkReadyWithQueue()
        return
      }
      R.pendingTune = false
      if (s.bootResume && s.audio !== 'unlocked') {
        // Live boot cues nothing under the gate — doUnlock lands the live/resume
        // intent on whack/probe (no stale-program flash). Non-live resume pre-cues.
        if (!R.wantLive) cueResume()
        checkReadyWithQueue()
        return
      }
      const ch = current()
      if (ch && !ch.noSignal && s.audio !== 'unlocked') cueSource(ch)
      // Unlocked already (warm-tube intent persists) → the reconciler lands it.
      checkReadyWithQueue()
    }

    // The current program changed (swap / advance / jump). The banner/guide read
    // now-playing from programIndex + queue, so we just sync the index here.
    const onDualProgram = (p: { index: number; total: number; title: string }) => {
      if (p.index >= 0 && p.index !== stateRef.current.programIndex) {
        D({ type: 'SET_PROGRAM', index: p.index })
        bumpSync()
      }
    }

    // ANY frame moved (active or standby): the reactive tap. Re-run the reconciler so
    // it converges to the desired frame — this is what drives auto-advance (a foreground
    // ENDED → advance the manual intent → swap), reveals (PLAYING → hide the cover), and
    // recovery, with no per-path handlers. A genuine play/buffer while still awaiting the
    // gesture IS the whack — unlock first, then the unlock lands the intent.
    const onDualChange = () => {
      const s = stateRef.current
      if (s.audio === 'awaiting-gesture') {
        const st = R.dual?.snapshot().fgState
        if (st === 1 || st === 3) {
          doUnlock()
          return
        }
      }
      runPlayerSnapshot()
    }

    // A video failed after the DualPlayer's retries. A SINGLE failure = a dead video: mark it dead
    // (computeDesired routes around it) and reconcile to the next. But CONSECUTIVE failures with no
    // genuine play between = YouTube blocking us systemically (IP bot-flag → error 150 on
    // everything); marking each dead just churns the whole queue and swaps the iframe out from under
    // the viewer. At the threshold, enter BLOCKED: stop advancing, free the dead-marks (they're not
    // really dead), and let runReconcile freeze + drop the click-catch so the viewer can interact.
    const onDualError = (videoId: string) => {
      TVAudio.staticBurst(0.12, 0.25)
      applyPlaybackEvent({ t: 'Error', videoId })
    }

    /* ---- empirical autoplay-with-sound probe (silent WAV) ---- */
    const silentClip = () => {
      if (R.silentUrl) return R.silentUrl
      const sr = 8000
      const n = Math.floor(sr * 0.05)
      const buf = new ArrayBuffer(44 + n * 2)
      const dv = new DataView(buf)
      const ws = (o: number, str: string) => {
        for (let i = 0; i < str.length; i++) dv.setUint8(o + i, str.charCodeAt(i))
      }
      ws(0, 'RIFF')
      dv.setUint32(4, 36 + n * 2, true)
      ws(8, 'WAVE')
      ws(12, 'fmt ')
      dv.setUint32(16, 16, true)
      dv.setUint16(20, 1, true)
      dv.setUint16(22, 1, true)
      dv.setUint32(24, sr, true)
      dv.setUint32(28, sr * 2, true)
      dv.setUint16(32, 2, true)
      dv.setUint16(34, 16, true)
      ws(36, 'data')
      dv.setUint32(40, n * 2, true)
      R.silentUrl = URL.createObjectURL(new Blob([buf], { type: 'audio/wav' }))
      return R.silentUrl
    }
    const probeAudioAllowed = () =>
      new Promise<boolean>((resolve) => {
        try {
          const a = document.createElement('audio')
          a.src = silentClip()
          a.volume = 0.0001
          a.muted = false
          a.setAttribute('playsinline', '')
          const settle = (v: boolean) => {
            try {
              a.pause()
            } catch {
              /* ignore */
            }
            resolve(v)
          }
          const p = a.play()
          if (p && typeof p.then === 'function') p.then(() => settle(true), () => settle(false))
          else settle(false)
        } catch {
          resolve(false)
        }
      })

    /* ---- boot: replicate renderInitial()'s post-mount logic ---- */
    const boot = () => {
      const s = stateRef.current
      setTheme(current())
      if (s.power === 'off') {
        setScreen('off')
        setGlow(0)
        return
      }
      if (s.bootResume) {
        R.armed = true
        R.pendingTune = true
        syncLegacyFromModel({ ...R.model, wantLive: s.bootResume.live ?? true }) // resume goes live unless a URL pinned a program
        setGlow(s.screen === 'snow' ? 0.18 : 0.5)
        flashOsd('channel')
        // Coming up LIVE (the powered-on default): do NOT pre-cue the stale persisted
        // program — doUnlock builds and jumps the live queue directly. Pre-cuing it
        // is what flashed a YouTube error / old thumbnail before the live jump.
        if (tubeReadyRef.current && !R.wantLive) cueResume()
        watchForWhack()
        probeAudioAllowed().then((allowed) => {
          if (allowed && stateRef.current.audio !== 'unlocked') doUnlock(true)
        })
      } else {
        tuneTo(s.channelIndex, {})
      }
      startNoSignalBounce()
    }

    /* ---- node registration callbacks ---- */
    const registerTv = (node: HTMLDivElement | null) => {
      R.tvNode = node
    }
    const registerScreen = (node: HTMLDivElement | null) => {
      R.screenNode = node
    }
    const registerNoSignalBox = (node: HTMLDivElement | null) => {
      R.nsBox = node
    }
    // Two player hosts (A active, B standby). DualPlayer is built once BOTH are
    // mounted, then self-drives the queue across them.
    const maybeBuildDual = () => {
      if (R.dual || !R.playerHostA || !R.playerHostB) return
      // The Stage's two non-video presentables, injected so the engine layer stays Three/React-free:
      //  • scene → the existing OverlayDirector bump machinery (ad / ad-channel / the tune cover);
      //  • component → the in-tree React mount (it needs TvContext), driven by the activeComponent signal.
      // The Stage calls teardown() on these as part of every transition — owned, so no stale layer.
      const presenters = {
        scene: {
          show: (scene: string, params: Record<string, unknown>, startSec: number) =>
            showSceneBump(R.bumpChannel ?? current(), { scene, params, startSec }),
          teardown: () => hideSceneBump(),
        },
        component: {
          show: (component: string) => D({ type: 'SET_COMPONENT', component }),
          teardown: () => D({ type: 'SET_COMPONENT', component: null }),
        },
        // the now-playing card: the engine SHOWS it; the Stage tears it down on any transition so it can't
        // linger past a channel change / NEXT / PREV (the buffer flip removes the overlay, structurally).
        card: { teardown: () => hideCardBump() },
      }
      R.dual = new DualPlayer(R.playerHostA, R.playerHostB, {
        onReady: onDualReady,
        onProgram: onDualProgram,
        onChange: onDualChange,
        onError: onDualError,
      }, presenters)
      R.dual.init().catch(() => {})
    }
    const registerPlayerHostA = (node: HTMLDivElement | null) => {
      R.playerHostA = node
      if (node) maybeBuildDual()
    }
    const registerPlayerHostB = (node: HTMLDivElement | null) => {
      R.playerHostB = node
      if (node) maybeBuildDual()
    }
    const registerSnow = (node: HTMLCanvasElement | null) => {
      R.snowNode = node
      if (node && !R.snow) {
        R.snow = new Snow(node)
        const sc = stateRef.current.screen
        if (sc === 'snow' || sc === 'tuning') R.snow.start()
      }
    }
    const registerRemote = (node: HTMLDivElement | null) => {
      R.remote = node
      if (node) applyRemotePos()
    }
    const registerBumpCanvas = (node: HTMLCanvasElement | null) => {
      R.bumpCanvas = node
      if (node) ensureBumpRunner()
    }
    const registerBumpOverlay = (node: HTMLElement | null) => {
      R.bumpOverlay = node
      if (node) ensureBumpRunner()
    }

    return {
      act,
      guideSelectAndTune,
      guidePlayBlock,
      guidePlayChannelLive,
      guideZoom,
      guideSearchSet,
      remoteCollapse,
      whackScreen,
      startRemoteDrag,
      applyRemotePos,
      applyFsTransform,
      syncNativeFullscreen,
      pointerNearBottom,
      remoteActivate,
      remoteKeepAlive,
      setTouchScreen,
      setRemoteScale,
      toggleSetting,
      adConfirmPointerMove,
      adConfirmIdleTick,
      confirmAdBumpersOff,
      toggleSubtitles,
      dismissExtensionHint,
      reportBug,
      playProgram,
      shuffleToggle,
      boot,
      registerTv,
      registerScreen,
      registerPlayerHostA,
      registerPlayerHostB,
      registerSnow,
      registerNoSignalBox,
      registerRemote,
      registerBumpCanvas,
      registerBumpOverlay,
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [channels, machine])

  /* ---- effects: wire the controller to the document lifecycle ---- */
  const adBumperConfirmVisible = state.adBumperConfirm !== null

  // Boot once, after the first commit (nodes are registered by then).
  useEffect(() => {
    ctrl.boot()
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Snow runs while the screen shows snow or is tuning.
  useEffect(() => {
    const snow = R.snow
    if (!snow) return
    if (state.screen === 'snow' || state.screen === 'tuning') snow.start()
    else snow.stop()
  }, [state.screen, R.snow])

  // Device class: a touch (coarse-pointer) device gets `is-touch` (drives every mobile affordance),
  // and `is-portrait` tracks orientation. Set once + kept in sync via the media queries themselves.
  useEffect(() => {
    if (typeof window === 'undefined' || !window.matchMedia) return
    const coarse = window.matchMedia('(pointer: coarse)')
    const portrait = window.matchMedia('(orientation: portrait)')
    const apply = () => {
      const classes = deviceBodyClasses({ coarse: coarse.matches, portrait: portrait.matches })
      document.body.classList.toggle('is-touch', classes.includes('is-touch'))
      document.body.classList.toggle('is-portrait', classes.includes('is-portrait'))
    }
    apply()
    coarse.addEventListener('change', apply)
    portrait.addEventListener('change', apply)
    return () => { coarse.removeEventListener('change', apply); portrait.removeEventListener('change', apply) }
  }, [])

  // Touch: scale the remote SHEET so it stays out of the TV PICTURE. (offsetHeight ignores the scale,
  // so the measurement is stable — no feedback loop.)
  //  • LANDSCAPE — the remote docks on the left, vertically centred, so it just has to fit the viewport
  //    HEIGHT; a short phone shrinks it (--remote-fit < 1) instead of clipping.
  //  • PORTRAIT — the bottom sheet must not cover the video. Assume a 16:9 picture centred in the
  //    viewport (videoH = innerWidth·9/16) and fit the sheet into the empty band BELOW it (half the
  //    non-video height). On a very tall phone that band is large → fit stays 1 (unchanged).
  useEffect(() => {
    const fitRemote = () => {
      const node = R.remote
      if (!node) return
      if (!isTouch()) { node.style.removeProperty('--remote-fit'); return }
      const h = node.offsetHeight
      if (!h) return
      const portrait = !!window.matchMedia && window.matchMedia('(orientation: portrait)').matches
      const videoH = window.innerWidth * 9 / 16
      const avail = portrait
        ? Math.max(0, (window.innerHeight - videoH) / 2 - 12) // the band below a centred 16:9 picture
        : window.innerHeight - 24
      const f = Math.max(0.5, Math.min(1, avail / h))
      node.style.setProperty('--remote-fit', String(Math.round(f * 1000) / 1000))
    }
    fitRemote()
    window.addEventListener('resize', fitRemote)
    window.addEventListener('orientationchange', fitRemote)
    return () => { window.removeEventListener('resize', fitRemote); window.removeEventListener('orientationchange', fitRemote) }
    // re-fit when the remote's height can change (touchscreen tab, collapse) or it opens
    // eslint-disable-next-line react-hooks/exhaustive-deps -- R is a stable ref container
  }, [state.touchScreen, state.remoteSheetOpen, state.remoteCollapsed])

  // Body classes the prototype toggled (power LED + remote lock).
  useEffect(() => {
    document.body.classList.toggle('is-on', state.power === 'on')
    document.body.classList.toggle('is-standby', state.power === 'off')
    document.body.classList.toggle('audio-locked', state.audio !== 'unlocked')
    document.body.classList.toggle('is-fullscreen', state.fullscreen)
    document.body.classList.toggle('remote-sheet-open', state.remoteSheetOpen)
    document.body.classList.toggle('remote-collapsed', state.remoteCollapsed)
  }, [state.power, state.audio, state.fullscreen, state.remoteSheetOpen, state.remoteCollapsed])

  // Display settings → body classes. CSS reacts (each "no-*" class strips a
  // layer of the CRT treatment); bareVideo is the master "just the video".
  useEffect(() => {
    const s = state.settings
    const b = document.body.classList
    b.toggle('no-scanlines', !s.scanlines)
    b.toggle('no-curvature', !s.curvature)
    b.toggle('no-ambient', !s.ambient)
    b.toggle('no-crt', !s.crt)
    b.toggle('bare-video', s.bareVideo)
    b.toggle('no-overscan', !s.overscan)
  }, [state.settings])

  // Optional local extension: it runs inside YouTube embed frames and suppresses
  // YouTube chrome we cannot reach from the page. The app stays fully usable
  // without it; this only controls the small suggestion in the tube.
  useEffect(() => {
    const markAvailable = () => dispatch({ type: 'YOUTUBE_CLEANER', available: true })
    document.addEventListener('orion-youtube-cleaner-ready', markAvailable)
    if (document.documentElement.dataset.orionYoutubeCleaner === 'ready') markAvailable()
    const t = window.setTimeout(() => {
      if (document.documentElement.dataset.orionYoutubeCleaner !== 'ready') {
        dispatch({ type: 'YOUTUBE_CLEANER', available: false })
      }
    }, 900)
    return () => {
      window.clearTimeout(t)
      document.removeEventListener('orion-youtube-cleaner-ready', markAvailable)
    }
  }, [])

  // Keep state.fullscreen in step with the NATIVE fullscreen (true-fs path) — incl. ESC.
  useEffect(() => {
    const onFsChange = () => ctrl.syncNativeFullscreen()
    document.addEventListener('fullscreenchange', onFsChange)
    return () => document.removeEventListener('fullscreenchange', onFsChange)
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Fullscreen: watch the pointer for the bottom-edge peek + keep the zoom fitted on
  // resize. Only for the in-window zoom — native (true) fullscreen sizes itself, so skip.
  useEffect(() => {
    if (!state.fullscreen || R.usingNativeFs) return
    const onMove = (e: PointerEvent) => {
      ctrl.pointerNearBottom(e.clientY >= window.innerHeight - 90, e.clientX)
    }
    const onResize = () => ctrl.applyFsTransform(true, false)
    window.addEventListener('pointermove', onMove)
    window.addEventListener('resize', onResize)
    return () => {
      window.removeEventListener('pointermove', onMove)
      window.removeEventListener('resize', onResize)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state.fullscreen])

  // The ad-disable confirm button can leave the touchscreen, so it listens to
  // document-level pointer movement while visible.
  useEffect(() => {
    if (!adBumperConfirmVisible) return
    let lastPointer: { clientX: number; clientY: number } | null = null
    // Coalesce pointer bursts to at most one flee dispatch per animation frame —
    // pointermove can fire well above 60/s on high-refresh devices, and each
    // dispatch re-renders the store's consumers. One per frame is plenty.
    let raf = 0
    const onMove = (e: PointerEvent) => {
      lastPointer = { clientX: e.clientX, clientY: e.clientY }
      if (raf) return
      raf = window.requestAnimationFrame(() => {
        raf = 0
        if (lastPointer) ctrl.adConfirmPointerMove(lastPointer)
      })
    }
    // Idle wander when the cursor isn't chasing it.
    const idle = window.setInterval(() => ctrl.adConfirmIdleTick(lastPointer), AD_CONFIRM_IDLE_MS)
    document.addEventListener('pointermove', onMove)
    return () => {
      document.removeEventListener('pointermove', onMove)
      window.clearInterval(idle)
      if (raf) window.cancelAnimationFrame(raf)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [adBumperConfirmVisible])

  // Keep the runaway confirm copy changing while it is on screen.
  useEffect(() => {
    if (!adBumperConfirmVisible) return
    const pickLabel = () => {
      const current = stateRef.current.adBumperConfirm?.label
      const choices = AD_CONFIRM_LABELS.filter((label) => label !== current)
      const label = choices[Math.floor(Math.random() * choices.length)] ?? AD_CONFIRM_LABELS[0]
      dispatch({ type: 'AD_BUMPER_CONFIRM_LABEL', label })
    }
    const t = window.setInterval(pickLabel, 2500)
    return () => window.clearInterval(t)
  }, [adBumperConfirmVisible])

  // Persist the preference slice whenever it changes. We intentionally key on
  // only the persisted fields (not the whole state) so live-program/clock/OSD
  // churn doesn't hammer localStorage; savePersisted reads via stateRef.
  useEffect(() => {
    savePersisted(stateRef.current, channels)
  }, [
    state.power,
    state.channelIndex,
    state.programIndex,
    state.volume,
    state.muted,
    state.subtitles,
    state.remotePos,
    state.remoteScale,
    state.settings,
    channels,
  ])

  // WRITE-only URL sync: reflect the current channel/position in the address bar (no navigation).
  // The URL is only READ on boot (initialState); from then on it's a shareable mirror of state.
  useEffect(() => {
    syncUrl(stateRef.current, channels)
  }, [state.power, state.channelIndex, state.programIndex, state.live, channels])

  // Keyboard + global gesture wake + iframe-focus whack backup + resize.
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.metaKey || e.ctrlKey || e.altKey) return
      // While the guide search box is focused it owns every key (typing + its own arrow/enter/escape
      // handling) — don't let the global shortcuts (digits → tune, g → guide, etc.) fire underneath.
      if (document.activeElement instanceof HTMLElement && document.activeElement.dataset.guideSearch != null) return
      const k = e.key
      if (/^[0-9]$/.test(k)) {
        e.preventDefault()
        ctrl.act('digit', parseInt(k, 10))
        return
      }
      if (stateRef.current.guideOpen) {
        if (k === 'ArrowUp') {
          e.preventDefault()
          ctrl.act('chUp')
          return
        }
        if (k === 'ArrowDown') {
          e.preventDefault()
          ctrl.act('chDown')
          return
        }
      }
      const a = KEYMAP[k]
      if (!a) return
      e.preventDefault()
      ctrl.act(a)
    }
    // In TRUE fullscreen the YouTube iframe keeps re-grabbing keyboard focus, so its keystrokes stay
    // in that cross-origin frame and our keydown listener never fires (no shortcuts work). Reclaim
    // focus onto our screen whenever the iframe takes it — EXCEPT while blocked, where the viewer
    // needs the iframe to sign in past YouTube's bot-check.
    const onFocusIn = () => {
      const s = stateRef.current
      if (!s.nativeFs || s.blocked) return
      if (document.activeElement instanceof HTMLIFrameElement) R.screenNode?.focus()
    }
    const onResize = () => ctrl.applyRemotePos()
    const onBlur = () => {
      // window blur + activeElement === iframe is the proven "the YouTube iframe grabbed focus"
      // signal in this app (also used for the unlock whack). Use it to reclaim keyboard focus in
      // true fullscreen so shortcuts keep working (cross-origin focusin doesn't fire everywhere).
      window.setTimeout(() => {
        const s = stateRef.current
        const onIframe = document.activeElement instanceof HTMLIFrameElement
        if (s.audio === 'awaiting-gesture') {
          const ifr = R.dual?.getActiveIframe?.()
          if (ifr && document.activeElement === ifr) ctrl.whackScreen()
          return
        }
        if (s.nativeFs && !s.blocked && onIframe) R.screenNode?.focus()
      }, 0)
    }
    // Touch swipes on the picture: vertical = channel, horizontal = volume. A below-threshold touch is
    // a tap → the existing whack/play. Only over the screen, only while playback owns the picture.
    let swX = 0, swY = 0, swTracking = false
    const onTouchStart = (e: TouchEvent) => {
      swTracking = isTouch() && e.touches.length === 1 && !!R.screenNode?.contains(e.target as Node)
      if (swTracking) { swX = e.touches[0].clientX; swY = e.touches[0].clientY }
    }
    const onTouchEnd = (e: TouchEvent) => {
      if (!swTracking) return
      swTracking = false
      if (stateRef.current.audio !== 'unlocked') return
      const t = e.changedTouches[0]
      if (!t) return
      const action = classifySwipe(t.clientX - swX, t.clientY - swY)
      if (!action) return // a tap → let the click toggle play
      e.preventDefault() // swallow the compatibility click so the tap-to-play doesn't also fire
      ctrl.act(action)
    }
    document.addEventListener('keydown', onKey)
    document.addEventListener('focusin', onFocusIn)
    document.addEventListener('touchstart', onTouchStart, { passive: true })
    document.addEventListener('touchend', onTouchEnd, { passive: false })
    window.addEventListener('resize', onResize)
    window.addEventListener('blur', onBlur)
    return () => {
      document.removeEventListener('keydown', onKey)
      document.removeEventListener('focusin', onFocusIn)
      document.removeEventListener('touchstart', onTouchStart)
      document.removeEventListener('touchend', onTouchEnd)
      window.removeEventListener('resize', onResize)
      window.removeEventListener('blur', onBlur)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  const api: TvApi = {
    state,
    channels,
    current:
      state.offAirNumber != null
        ? offAirChannel(state.offAirNumber, channels.find((ch) => ch.noSignal) ?? channels[0])
        : channels[state.channelIndex],
    act: ctrl.act,
    guideSelectAndTune: ctrl.guideSelectAndTune,
    guidePlayBlock: ctrl.guidePlayBlock,
    guidePlayChannelLive: ctrl.guidePlayChannelLive,
    guideZoom: ctrl.guideZoom,
    guideSearchSet: ctrl.guideSearchSet,
    remoteCollapse: ctrl.remoteCollapse,
    whackScreen: ctrl.whackScreen,
    startRemoteDrag: ctrl.startRemoteDrag,
    remoteActivate: ctrl.remoteActivate,
    remoteKeepAlive: ctrl.remoteKeepAlive,
    setTouchScreen: ctrl.setTouchScreen,
    setRemoteScale: ctrl.setRemoteScale,
    toggleSetting: ctrl.toggleSetting,
    adConfirmPointerMove: ctrl.adConfirmPointerMove,
    adConfirmIdleTick: ctrl.adConfirmIdleTick,
    confirmAdBumpersOff: ctrl.confirmAdBumpersOff,
    toggleSubtitles: ctrl.toggleSubtitles,
    dismissExtensionHint: ctrl.dismissExtensionHint,
    reportBug: ctrl.reportBug,
    playProgram: ctrl.playProgram,
    shuffleToggle: ctrl.shuffleToggle,
    registerTv: ctrl.registerTv,
    registerScreen: ctrl.registerScreen,
    registerPlayerHostA: ctrl.registerPlayerHostA,
    registerPlayerHostB: ctrl.registerPlayerHostB,
    registerSnow: ctrl.registerSnow,
    registerNoSignalBox: ctrl.registerNoSignalBox,
    registerRemote: ctrl.registerRemote,
    registerBumpCanvas: ctrl.registerBumpCanvas,
    registerBumpOverlay: ctrl.registerBumpOverlay,
  }

  return <TvContext.Provider value={api}>{children}</TvContext.Provider>
}
